/* ==========================================================================
   Moonbow Skin â Phase 1 behaviour
   Header, product main section and footer.

   Dependency-free. Every controller is idempotent and re-runs on the Shopify
   Theme Editor's section lifecycle events, so sections can be added, removed
   and reordered without a page refresh.
   ========================================================================== */
(function () {
  'use strict';

  var INIT_FLAG = '__mbInit';

  /* ---------------------------------------------------------------- utils */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function once(el, key) {
    if (!el) return false;
    el[INIT_FLAG] = el[INIT_FLAG] || {};
    if (el[INIT_FLAG][key]) return false;
    el[INIT_FLAG][key] = true;
    return true;
  }

  function readJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function formatMoney(cents) {
    var format =
      (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var value = Number(cents) || 0;

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1' + thousands
      );
      var cents = parts[1] ? decimal + parts[1] : '';
      return whole + cents;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount':
          return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_space_separator':
          return withDelimiters(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return withDelimiters(value, 0, ' ', ',');
        default:
          return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  function routes() {
    return (window.theme && window.theme.routes) || {};
  }

  var CART_SECTIONS = 'cart-items,cart-footer,cart-item-count,cart-live-region';

  /**
   * Page scroll lock, reference counted.
   *
   * The mobile drawer and the image lightbox can both be open at once (open
   * the drawer, close it while the lightbox is up) and a plain
   * `overflow = ''` from whichever closes first would hand the page back to
   * the scroll it is still meant to be holding. Counting the holders means
   * the last release is the one that restores the page, and the original
   * inline value is put back rather than blanked.
   */
  var scrollLock = (function () {
    var holders = 0;
    var previous = '';
    var previousPad = '';

    return {
      lock: function () {
        holders += 1;
        if (holders > 1) return;
        var root = document.documentElement;
        /* A classic (non-overlay) scrollbar disappears with the overflow;
           its width goes back as padding so the page does not shift
           sideways while a drawer or the zoom is open. */
        var bar = window.innerWidth - root.clientWidth;
        previous = root.style.overflow;
        previousPad = root.style.paddingRight;
        root.style.overflow = 'hidden';
        if (bar > 0) root.style.paddingRight = bar + 'px';
      },
      release: function () {
        if (holders === 0) return;
        holders -= 1;
        if (holders > 0) return;
        document.documentElement.style.overflow = previous;
        document.documentElement.style.paddingRight = previousPad;
      }
    };
  })();

  /**
   * Opens the theme's own cart drawer. Shapes registers it against the Alpine
   * `modals` store, and its stock header calls `$store.modals.open('cart')` â
   * this is the same call from outside an Alpine scope. Returns false when the
   * drawer isn't available so the caller can fall back to the cart page.
   */
  function openThemeCartDrawer() {
    var alpine = window.Alpine;
    if (!alpine || typeof alpine.store !== 'function') return false;
    var modals = alpine.store('modals');
    if (!modals || typeof modals.open !== 'function') return false;
    try {
      modals.open('cart');
      return true;
    } catch (e) {
      return false;
    }
  }

  function setCartCount(count) {
    $$('[data-mb-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = !count;
    });
  }

  function refreshCartCount() {
    fetch((routes().cart_url || '/cart') + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        if (cart) setCartCount(cart.item_count);
      })
      .catch(function () {});
  }

  /* Any cart change anywhere on the page keeps the header badge honest. */
  ['shapes:cart:afteradditem', 'shapes:cart:update', 'shapes:cart:cartqtychange'].forEach(
    function (name) {
      document.addEventListener(name, refreshCartCount);
      document.body && document.body.addEventListener(name, refreshCartCount);
    }
  );

  /**
   * Hands a freshly added item to the theme's own cart pipeline. The Shapes
   * cart drawer listens for `shapes:cart:afteradditem` and re-renders itself
   * from `detail.response.sections`, so we reuse that contract rather than
   * touching the drawer directly.
   */
  function announceCartAdd(item) {
    return fetch(window.location.pathname + '?sections=' + CART_SECTIONS)
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (sections) {
        document.body.dispatchEvent(
          new CustomEvent('shapes:cart:afteradditem', {
            bubbles: true,
            detail: { response: { sections: sections || {} }, item: item }
          })
        );
        document.dispatchEvent(new CustomEvent('theme:update:cart'));
        refreshCartCount();
      })
      .catch(function () {
        /* The drawer just won't refresh; the item is already in the cart. */
      });
  }

  function addToCart(body) {
    return fetch(routes().cart_add_url || '/cart/add', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          throw new Error(
            data.description ||
              data.message ||
              (window.theme &&
                window.theme.strings &&
                window.theme.strings.cartAddError) ||
              'Could not add to cart'
          );
        }
        return data;
      });
    });
  }

  /* ------------------------------------------------------------ header nav */

  function initHeader(root) {
    $$('[data-mb-header]', root).forEach(function (header) {
      if (!once(header, 'header')) return;

      if (header.classList.contains('mb-header-wrap--sticky')) initSticky(header);

      /* The dropdown indicator is rendered from the Shopify menu in Liquid.
         This re-checks it against the DOM so an item can never keep a caret
         when no submenu was actually rendered for it. */
      $$('.mb-header__item', header).forEach(function (item) {
        var dropdown = item.querySelector('.mb-header__dropdown');
        if (dropdown && dropdown.querySelector('a')) return;

        item.removeAttribute('data-mb-has-dropdown');
        if (dropdown) dropdown.remove();
        var caret = item.querySelector('.mb-header__caret');
        if (caret) caret.remove();
        var stale = item.querySelector('[data-mb-dropdown-trigger]');
        if (stale) {
          stale.removeAttribute('data-mb-dropdown-trigger');
          stale.removeAttribute('aria-expanded');
          stale.removeAttribute('aria-haspopup');
        }
      });

      /* Cart: open the theme's drawer when it has one, otherwise let the
         link navigate to the cart page. */
      var usesDrawer = header.getAttribute('data-cart-drawer') === 'true';
      $$('[data-mb-cart-link]', header).forEach(function (link) {
        link.addEventListener('click', function (event) {
          if (!usesDrawer) return;
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
          if (openThemeCartDrawer()) event.preventDefault();
        });
      });

      /* Search: the icon toggles the panel; with JS off it stays a plain
         link to /search and the panel's form still posts to /search. */
      var searchPanel = $('[data-mb-search]', header);
      if (searchPanel) initSearch(header, searchPanel);

      /* Desktop dropdowns: hover is CSS, this adds click + keyboard. */
      $$('[data-mb-has-dropdown]', header).forEach(function (item) {
        var trigger = $('[data-mb-dropdown-trigger]', item);
        if (!trigger) return;

        trigger.addEventListener('click', function (event) {
          // Let the top-level link work when it points somewhere real and the
          // menu is already open (second click = navigate).
          if (!item.classList.contains('is-open')) {
            event.preventDefault();
          }
          closeAllDropdowns(header, item);
          item.classList.toggle('is-open');
          trigger.setAttribute(
            'aria-expanded',
            item.classList.contains('is-open') ? 'true' : 'false'
          );
        });
      });

      document.addEventListener('click', function (event) {
        if (!header.contains(event.target)) closeAllDropdowns(header);
      });

      header.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeAllDropdowns(header);
      });

      /* Drawers: the mobile menu and the wishlist. Each opener names its
         drawer with `aria-controls`; one without it opens the first drawer
         (the menu), as before. */
      $$('[data-mb-drawer]', header).forEach(function (drawer, drawerIndex) {
        var openers = $$('[data-mb-drawer-open]', header).filter(function (btn) {
          var target = btn.getAttribute('aria-controls');
          return target ? target === drawer.id : drawerIndex === 0;
        });
        var closers = $$('[data-mb-drawer-close]', drawer);

        function setDrawer(open) {
          if (open === drawer.classList.contains('is-open')) return;
          drawer.classList.toggle('is-open', open);
          drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
          openers.forEach(function (btn) {
            btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          });
          if (open) {
            scrollLock.lock();
            drawer.dispatchEvent(new CustomEvent('mb:drawer:open', { bubbles: true }));
          } else {
            scrollLock.release();
          }
          if (open) {
            var first = $('a, button', drawer);
            if (first) first.focus();
          }
        }

        openers.forEach(function (btn) {
          btn.addEventListener('click', function () {
            setDrawer(true);
          });
        });
        closers.forEach(function (btn) {
          btn.addEventListener('click', function () {
            setDrawer(false);
          });
        });
        drawer.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') setDrawer(false);
        });

        $$('[data-mb-drawer-toggle]', drawer).forEach(function (btn) {
          btn.addEventListener('click', function () {
            var open = btn.getAttribute('aria-expanded') === 'true';
            btn.setAttribute('aria-expanded', open ? 'false' : 'true');
          });
        });
      });
    });
  }

  function closeAllDropdowns(header, except) {
    $$('[data-mb-has-dropdown]', header).forEach(function (item) {
      if (item === except) return;
      item.classList.remove('is-open');
      var trigger = $('[data-mb-dropdown-trigger]', item);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }



  /* ---------------------------------------------------------------- sticky */

  /**
   * A sticky box can only move within its own containing block, and
   * `#shopify-section-header` is exactly as tall as the header â so the
   * position: sticky has to go on that wrapper. We also flag the stuck state
   * so the card can take back the top border the announcement bar was
   * providing while it was still on screen.
   */
  function initSticky(header) {
    var host = header.parentElement;
    if (!host) return;
    host.classList.add('mb-sticky-host');

    /*
      The promo bar directly above the header sticks with it. Its height is
      the header's sticky offset, published as `--mb-announce-h` and kept
      current as it changes (breakpoints, fonts), 0 when there is no bar.
    */
    var announce = host.previousElementSibling;
    /* Two announcement sections share the slot (scrolling and static), and
       on any page one of them renders nothing - step over an empty one to
       the bar that is actually there. */
    while (
      announce &&
      !announce.querySelector('.mb-announcement-shell') &&
      announce.classList.contains('section-moonbow-announcement') &&
      !announce.getBoundingClientRect().height
    ) {
      announce = announce.previousElementSibling;
    }
    if (announce && !announce.querySelector('.mb-announcement-shell')) announce = null;
    var announceH = null;
    function publishAnnounce() {
      var h = announce ? Math.round(announce.getBoundingClientRect().height) : 0;
      if (h === announceH) return;
      announceH = h;
      document.documentElement.style.setProperty('--mb-announce-h', h + 'px');
    }
    if (announce) {
      announce.classList.add('mb-sticky-announce');
      if (window.ResizeObserver) new ResizeObserver(publishAnnounce).observe(announce);
    }
    publishAnnounce();

    // The host is pinned at exactly its sticky offset while it is stuck, so
    // its own position is the signal â no sentinel element needed. The
    // offset comes from the stylesheet so the breakpoint lives in one place.
    // One rect read per scroll event on a single element is cheap, and the
    // classes are only touched when the state actually flips.
    var stuck = null;

    function offset() {
      var top = parseFloat(getComputedStyle(host).top);
      return isNaN(top) ? 0 : top;
    }

    function sync() {
      var next = host.getBoundingClientRect().top <= offset() + 0.5;
      if (next === stuck) return;
      stuck = next;
      header.classList.toggle('is-stuck', next);
      // The host carries it too, so the cream cover above the card can be
      // driven from CSS without reaching back up the tree.
      host.classList.toggle('is-stuck', next);
    }

    /*
      Publish the bar's real height so anything that has to clear it can say
      so in CSS rather than carry a copy of the number. The sticky product
      gallery reads it; the fallback in the stylesheet is only ever used
      before this runs.
    */
    var measured = null;

    function publishHeight() {
      var h = Math.round(header.getBoundingClientRect().height);
      if (!h || h === measured) return;
      measured = h;
      document.documentElement.style.setProperty('--mb-header-h', h + 'px');
    }

    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', function () {
      sync();
      publishHeight();
    }, { passive: true });
    window.addEventListener('load', publishHeight);
    if (window.ResizeObserver) {
      new ResizeObserver(publishHeight).observe(header);
    }
    publishHeight();
    sync();
  }

  /* ---------------------------------------------------------------- search */

  /**
   * Header search: panel toggle plus live predictions.
   *
   * Predictions come from Shopify's Predictive Search through the Section
   * Rendering API â the same `/search/suggest?â¦&section_id=â¦` contract the
   * theme's own PredictiveSearch island uses â so every result is real store
   * data rendered by Liquid. See `sections/moonbow-predictive-search.liquid`.
   */
  function initSearch(header, panel) {
    var input = $('[data-mb-search-input]', panel);
    var results = $('[data-mb-search-results]', panel);
    var live = $('[data-mb-search-live]', panel);
    var form = $('form', panel);
    var toggles = $$('[data-mb-search-toggle]', header);

    var predictive = panel.hasAttribute('data-predictive') && !!results;
    var sectionId = panel.getAttribute('data-search-section');
    var resources = panel.getAttribute('data-search-resources') || 'product,query';
    var limit = panel.getAttribute('data-search-limit') || '6';

    var cache = {};
    var controller = null;
    var timer = null;
    var lastQuery = '';
    var options = [];
    var active = -1;

    /* ------------------------------------------------------------- panel */

    function setOpen(open) {
      panel.hidden = !open;
      toggles.forEach(function (t) {
        t.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      if (input) input.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        if (input) {
          input.focus();
          input.select();
          // Re-open with a term already typed (or on /search) â show results.
          if (predictive && input.value.trim()) schedule(0);
        }
      } else {
        clearResults();
      }
    }

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function (event) {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        setOpen(panel.hidden);
      });
    });

    $$('[data-mb-search-close]', panel).forEach(function (btn) {
      btn.addEventListener('click', function () {
        setOpen(false);
        if (toggles[0]) toggles[0].focus();
      });
    });

    document.addEventListener('click', function (event) {
      if (panel.hidden || header.contains(event.target)) return;
      setOpen(false);
    });

    if (!predictive) {
      header.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !panel.hidden) setOpen(false);
      });
      return;
    }

    /* --------------------------------------------------------- rendering */

    function clearResults() {
      if (controller) {
        controller.abort();
        controller = null;
      }
      clearTimeout(timer);
      lastQuery = '';
      results.innerHTML = '';
      results.hidden = true;
      panel.classList.remove('is-loading', 'has-results');
      collectOptions();
      if (live) live.textContent = '';
    }

    function collectOptions() {
      options = $$('[data-mb-pred-option]', results);
      setActive(-1);
    }

    function setActive(index) {
      active = index;
      options.forEach(function (el, i) {
        var on = i === index;
        el.setAttribute('aria-selected', on ? 'true' : 'false');
        el.classList.toggle('is-active', on);
        if (on && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      });
      if (input) {
        input.setAttribute(
          'aria-activedescendant',
          index > -1 && options[index] ? options[index].id || '' : ''
        );
      }
    }

    function paint(html) {
      results.innerHTML = html;
      results.hidden = false;
      panel.classList.add('has-results');
      collectOptions();
      var status = $('[data-mb-preds-status]', results);
      if (live) live.textContent = status ? status.textContent.trim() : '';
    }

    /* ------------------------------------------------------------ fetching */

    function endpoint(query) {
      var base = routes().predictive_search_url || '/search/suggest';
      return (
        base +
        '?q=' + encodeURIComponent(query) +
        '&' + encodeURIComponent('resources[type]') + '=' + encodeURIComponent(resources) +
        '&' + encodeURIComponent('resources[limit]') + '=' + encodeURIComponent(limit) +
        '&' + encodeURIComponent('resources[limit_scope]') + '=each' +
        '&section_id=' + encodeURIComponent(sectionId)
      );
    }

    function extract(markup) {
      var parsed = new DOMParser().parseFromString(markup, 'text/html');
      var node = parsed.querySelector('[data-mb-preds]');
      return node ? node.outerHTML : '';
    }

    function run(query) {
      if (cache[query] !== undefined) {
        lastQuery = query;
        panel.classList.remove('is-loading');
        paint(cache[query]);
        return;
      }

      if (controller) controller.abort();
      controller = typeof AbortController === 'function' ? new AbortController() : null;

      panel.classList.add('is-loading');
      // Keep whatever is on screen while the next page loads, but show the
      // skeleton when there is nothing to keep.
      if (!results.innerHTML) {
        results.hidden = false;
        results.innerHTML = skeleton();
      }

      fetch(endpoint(query), controller ? { signal: controller.signal } : undefined)
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (markup) {
          // A slower earlier request must not overwrite a newer query.
          if (query !== currentQuery()) return;
          var html = extract(markup);
          cache[query] = html;
          lastQuery = query;
          panel.classList.remove('is-loading');
          if (html) {
            paint(html);
          } else {
            results.innerHTML = '';
            results.hidden = true;
            panel.classList.remove('has-results');
            collectOptions();
          }
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          panel.classList.remove('is-loading');
          // Leave the form usable; submitting still reaches /search.
          results.innerHTML = '';
          results.hidden = true;
          panel.classList.remove('has-results');
          collectOptions();
        });
    }

    function currentQuery() {
      return input ? input.value.trim() : '';
    }

    function skeleton() {
      var rows = '';
      for (var i = 0; i < 3; i++) {
        rows +=
          '<li><span class="mb-preds__media"></span>' +
          '<span class="mb-preds__body"><span class="mb-preds__bar"></span>' +
          '<span class="mb-preds__bar mb-preds__bar--short"></span></span></li>';
      }
      return (
        '<div class="mb-preds mb-preds--skeleton" aria-hidden="true">' +
        '<ul class="mb-preds__products">' + rows + '</ul></div>'
      );
    }

    function schedule(delay) {
      clearTimeout(timer);
      var query = currentQuery();

      if (!query) {
        clearResults();
        return;
      }
      if (query === lastQuery && results.innerHTML) return;

      timer = setTimeout(function () {
        run(query);
      }, delay === undefined ? 160 : delay);
    }

    /* ------------------------------------------------------------- events */

    if (input) {
      input.addEventListener('input', function () {
        schedule();
      });
      input.addEventListener('focus', function () {
        if (currentQuery()) schedule(0);
      });
    }

    panel.addEventListener('keydown', function (event) {
      switch (event.key) {
        case 'Escape':
          if (active > -1) {
            setActive(-1);
          } else {
            setOpen(false);
            if (toggles[0]) toggles[0].focus();
          }
          event.preventDefault();
          break;

        case 'ArrowDown':
          if (!options.length) return;
          event.preventDefault();
          setActive(active + 1 >= options.length ? 0 : active + 1);
          break;

        case 'ArrowUp':
          if (!options.length) return;
          event.preventDefault();
          setActive(active - 1 < 0 ? options.length - 1 : active - 1);
          break;

        case 'Enter':
          // Enter follows the highlighted prediction, otherwise the form
          // submits to the full search results page as normal.
          if (active > -1 && options[active]) {
            event.preventDefault();
            options[active].click();
          }
          break;

        default:
          break;
      }
    });

    if (form) {
      form.addEventListener('submit', function () {
        setActive(-1);
      });
    }

    results.addEventListener('mousemove', function (event) {
      var option = event.target.closest ? event.target.closest('[data-mb-pred-option]') : null;
      if (!option) return;
      var index = options.indexOf(option);
      if (index > -1 && index !== active) setActive(index);
    });
  }

  /* --------------------------------------------------------------- gallery */

  function initGallery(root) {
    $$('[data-mb-gallery]', root).forEach(function (gallery) {
      if (!once(gallery, 'gallery')) return;

      var track = $('[data-mb-gallery-track]', gallery);
      if (!track) return;

      var slides = $$('[data-mb-gallery-slide]', track);
      var thumbs = $$('[data-mb-gallery-thumb]', gallery);
      var prev = $('[data-mb-gallery-prev]', gallery);
      var next = $('[data-mb-gallery-next]', gallery);
      var index = 0;

      function isCarousel() {
        // Mobile uses native scroll-snap; desktop uses a transformed track.
        return window.matchMedia('(max-width: 1023px)').matches;
      }

      var thumbWrap = $('.mb-gallery__thumbs', gallery);

      /*
        Phone loop. The phone gallery is a native scroll-snap row, which
        stops at its ends; a copy of the last image before the first and of
        the first after the last lets a swipe or an arrow carry on past
        either end in the same direction, and once it settles on a copy the
        row is re-seated, without animating, on the real image it shows -
        1 -> 2 -> 3 -> 4 -> 1, never back across the row. The copies are not
        slides (no `data-mb-gallery-slide`), so thumbnails, the zoom and the
        count never see them, and CSS hides them above 1024 where the desktop
        track loops on its own.
      */
      var edgeHead = null;
      var edgeTail = null;
      if (slides.length > 1) {
        var makeEdge = function (source) {
          var copy = source.cloneNode(true);
          copy.removeAttribute('data-mb-gallery-slide');
          copy.classList.add('mb-gallery__slide--clone');
          copy.setAttribute('aria-hidden', 'true');
          $$('[id]', copy).forEach(function (el) { el.removeAttribute('id'); });
          $$('a, button, video, iframe, [tabindex]', copy).forEach(function (el) {
            el.setAttribute('tabindex', '-1');
          });
          $$('video', copy).forEach(function (v) {
            v.removeAttribute('autoplay');
            v.muted = true;
          });
          return copy;
        };
        edgeHead = makeEdge(slides[slides.length - 1]);
        edgeTail = makeEdge(slides[0]);
        track.insertBefore(edgeHead, slides[0]);
        track.appendChild(edgeTail);
      }

      function centreOn(el, smooth) {
        if (!el) return;
        track.scrollTo({
          left: el.offsetLeft - (track.clientWidth - el.clientWidth) / 2,
          behavior: smooth ? 'smooth' : 'auto'
        });
      }

      function paint() {
        thumbs.forEach(function (thumb, i) {
          thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
        });

        /*
          The column shows five of however many thumbnails there are, so the
          active one can be below the fold after the arrows or a swipe. Nudge
          the column rather than the page - `scrollIntoView` would drag the
          whole document.
        */
        var active = thumbs[index];
        if (thumbWrap && active && thumbWrap.scrollHeight > thumbWrap.clientHeight) {
          /* Measured rather than read off `offsetTop`: the column is
             positioned, so the offset parent differs between the wrapper and
             the buttons inside it. */
          var aRect = active.getBoundingClientRect();
          var wRect = thumbWrap.getBoundingClientRect();
          var top = aRect.top - wRect.top + thumbWrap.scrollTop;
          var bottom = top + aRect.height;
          var to = null;
          if (top < thumbWrap.scrollTop) {
            to = top;
          } else if (bottom > thumbWrap.scrollTop + thumbWrap.clientHeight) {
            to = bottom - thumbWrap.clientHeight;
          }
          /*
            Set outright rather than animated: smooth programmatic scrolling
            is silently dropped by some engines (and by a reduced-motion
            setting), and a thumbnail column that quietly stops following the
            main image is the bug this is here to fix.
          */
          if (to !== null) {
            thumbWrap.scrollTop = to;
          }
        }
        if (prev) prev.disabled = slides.length < 2;
        if (next) next.disabled = slides.length < 2;
        gallery.dispatchEvent(
          new CustomEvent('mb:gallery:change', { detail: { index: index } })
        );
      }

      /*
        Desktop track position. `animate: false` jumps without the CSS
        transition (used to re-seat the track invisibly during a wrap).
      */
      function place(position, animate) {
        if (!animate) track.style.transition = 'none';
        track.style.transform = 'translateX(' + -position * 100 + '%)';
        if (!animate) {
          void track.offsetWidth;
          track.style.transition = '';
        }
      }

      /*
        The wrap from the last image to the first keeps moving the same way
        (1 -> 2 -> 3 -> 4 -> 1) instead of the track sliding back across
        every image. The first slide is moved to the end of the row with
        CSS `order`, the track is re-seated on the same picture without
        animating, and the ordinary slide animates onto it; once the
        transition ends the order is restored and the track re-seated at 0.
        Previous from the first image mirrors it. No slides are cloned, so
        the thumbnails and the zoom never see a duplicate.
      */
      var settleWrap = null;
      var settleTimer = null;

      function finishWrap() {
        clearTimeout(settleTimer);
        if (!settleWrap) return;
        var done = settleWrap;
        settleWrap = null;
        done();
      }

      track.addEventListener('transitionend', function (event) {
        if (event.target === track && event.propertyName === 'transform') finishWrap();
      });

      function go(target, options) {
        if (!slides.length) return;
        var count = slides.length;
        var from = index;
        index = ((target % count) + count) % count;
        var forwardWrap = count > 1 && from === count - 1 && target >= count;
        var backWrap = count > 1 && from === 0 && target < 0;
        var instant = options && options.instant;

        if (isCarousel()) {
          /* A move that starts while the row is still resting on an edge
             copy (the settle has not run yet - iOS momentum delays it)
             re-seats it first, so the next slide is always one step on,
             never a scroll back across the row. */
          reseat();
          /* Past either end the row slides on to the edge copy; the scroll
             settle below re-seats it on the real image. */
          if (!instant && forwardWrap && edgeTail) {
            centreOn(edgeTail, true);
          } else if (!instant && backWrap && edgeHead) {
            centreOn(edgeHead, true);
          } else {
            centreOn(slides[index], !instant);
          }
        } else {
          finishWrap();
          if (!instant && forwardWrap) {
            slides[0].style.order = '1';
            place(count - 2, false);
            place(count - 1, true);
            settleWrap = function () {
              slides[0].style.order = '';
              place(0, false);
            };
            settleTimer = setTimeout(finishWrap, 700);
          } else if (!instant && backWrap) {
            slides[count - 1].style.order = '-1';
            place(1, false);
            place(0, true);
            settleWrap = function () {
              slides[count - 1].style.order = '';
              place(count - 1, false);
            };
            settleTimer = setTimeout(finishWrap, 700);
          } else {
            place(index, !instant);
          }
        }
        paint();
      }

      if (prev) prev.addEventListener('click', function () { go(index - 1); });
      if (next) next.addEventListener('click', function () { go(index + 1); });

      /*
        Thumbnail column: a mouse drag scrolls it (touch keeps its native
        scroll), along whichever axis the column overflows. A press that
        moved more than a few pixels is a drag, and the click it ends with
        is swallowed so it never selects the thumbnail under the pointer; a
        press that did not move is an ordinary click.
      */
      var dragged = false;
      if (thumbWrap) {
        var drag = null;
        thumbWrap.addEventListener('pointerdown', function (event) {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          drag = {
            x: event.clientX,
            y: event.clientY,
            left: thumbWrap.scrollLeft,
            top: thumbWrap.scrollTop,
            id: event.pointerId,
            moved: false
          };
          dragged = false;
        });
        thumbWrap.addEventListener('pointermove', function (event) {
          if (!drag || event.pointerId !== drag.id) return;
          var dx = event.clientX - drag.x;
          var dy = event.clientY - drag.y;
          if (!drag.moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
          if (!drag.moved) {
            drag.moved = true;
            thumbWrap.classList.add('is-dragging');
            try { thumbWrap.setPointerCapture(drag.id); } catch (e) {}
          }
          if (thumbWrap.scrollHeight > thumbWrap.clientHeight) thumbWrap.scrollTop = drag.top - dy;
          if (thumbWrap.scrollWidth > thumbWrap.clientWidth) thumbWrap.scrollLeft = drag.left - dx;
          event.preventDefault();
        });
        var endDrag = function () {
          if (!drag) return;
          dragged = drag.moved;
          drag = null;
          thumbWrap.classList.remove('is-dragging');
          /* The click, if any, fires right after this; clear the flag once
             that turn is over. */
          setTimeout(function () { dragged = false; }, 0);
        };
        thumbWrap.addEventListener('pointerup', endDrag);
        thumbWrap.addEventListener('pointercancel', endDrag);
        /* Keep the browser's own image drag out of the way. */
        thumbWrap.addEventListener('dragstart', function (event) { event.preventDefault(); });
      }

      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function (event) {
          if (dragged) {
            event.preventDefault();
            return;
          }
          go(i);
        });
      });

      /* Once a swipe or an arrow settles on the phone: follow it with the
         index, and if it came to rest on an edge copy, re-seat the row on
         the real image that copy shows. */
      var scrollTimer = null;
      /* If the row is centred on an edge copy, jump it (no animation) onto
         the real image the copy shows. Returns that image's index, or -1. */
      function reseat() {
        if (!isCarousel() || !edgeHead) return -1;
        var center = track.scrollLeft + track.clientWidth / 2;
        var onCopy = [edgeHead, edgeTail].filter(function (el) {
          return Math.abs(el.offsetLeft + el.clientWidth / 2 - center) < el.clientWidth / 2;
        })[0];
        if (!onCopy) return -1;
        var real = onCopy === edgeTail ? 0 : slides.length - 1;
        var offset = center - (onCopy.offsetLeft + onCopy.clientWidth / 2);
        var target = slides[real];
        track.scrollTo({
          left: target.offsetLeft - (track.clientWidth - target.clientWidth) / 2 + offset,
          behavior: 'auto'
        });
        return real;
      }
      function settle() {
        if (!isCarousel()) return;
        var center = track.scrollLeft + track.clientWidth / 2;
        var candidates = [edgeHead].concat(slides, [edgeTail]).filter(Boolean);
        var closest = null;
        var best = Infinity;
        candidates.forEach(function (el) {
          var dist = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
          if (dist < best) {
            best = dist;
            closest = el;
          }
        });
        if (!closest) return;
        var next = index;
        if (closest === edgeTail) {
          next = 0;
          centreOn(slides[0], false);
        } else if (closest === edgeHead) {
          next = slides.length - 1;
          centreOn(slides[slides.length - 1], false);
        } else {
          next = slides.indexOf(closest);
        }
        if (next !== index) {
          index = next;
          paint();
        }
      }
      track.addEventListener('scroll', function () {
        if (!isCarousel()) return;
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(settle, 80);
      }, { passive: true });
      /* Where the browser reports the end of a scroll, settle at once. */
      track.addEventListener('scrollend', function () {
        if (!isCarousel()) return;
        clearTimeout(scrollTimer);
        settle();
      });

      /* The phone row starts on the first real image, not the copy before
         it. */
      function seatPhoneRow() {
        if (isCarousel()) centreOn(slides[index], false);
      }
      seatPhoneRow();
      window.addEventListener('load', seatPhoneRow, { once: true });

      /* Reset the desktop transform when crossing the breakpoint. */
      window.addEventListener('resize', function () {
        finishWrap();
        if (isCarousel()) {
          track.style.transform = '';
          seatPhoneRow();
        } else {
          place(index, false);
        }
      });

      /* Zoom â opens the lightbox on this page with every gallery image,
         starting at the one on show; closing it leaves the gallery on the
         image the viewer ended on. The wishlist button in the same corner
         is wired up by initWishlist, which owns every wishlist control on
         the page rather than just the gallery's. */
      initZoom(gallery, {
        slides: slides,
        current: function () { return index; },
        show: function (i) { go(i, { instant: true }); }
      });

      /*
        Gallery videos: the centred button plays (with sound - a click is
        the gesture browsers require, and `videoWall` keeps every other
        video on the page muted) and pauses. Whatever moves the gallery -
        arrows, thumbnails, a swipe settling, the lightbox, a colour change -
        ends in `paint()`, which fires `mb:gallery:change`; every video not
        on the new active slide is paused there, edge copies included. A
        video is never resumed on its own when its slide comes back.
      */
      $$('[data-mb-gallery-video]', track).forEach(function (box) {
        var video = $('video', box);
        var button = $('[data-mb-gallery-play]', box);
        if (!video || !button) return;
        video.removeAttribute('controls');
        videoWall.add(video);

        var mark = function () {
          box.classList.toggle('is-playing', !video.paused);
          button.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
        };
        ['play', 'pause', 'ended'].forEach(function (name) {
          video.addEventListener(name, mark);
        });
        button.addEventListener('click', function (event) {
          event.stopPropagation();
          if (video.paused) {
            videoWall.listen(video);
          } else {
            video.pause();
          }
          mark();
        });
        mark();
      });

      gallery.addEventListener('mb:gallery:change', function () {
        $$('video', track).forEach(function (video) {
          var slide = video.closest('.mb-gallery__slide');
          if (slide !== slides[index] && !video.paused) video.pause();
        });
      });

      /* A chosen colour's variant image becomes the main image
         (`initOptionPicker`). Media ids are on the slides already. */
      gallery.addEventListener('mb:gallery:show-media', function (event) {
        var id = String(event.detail && event.detail.mediaId);
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute('data-media-id') === id) {
            if (i !== index) go(i);
            return;
          }
        }
      });

      paint();
    });
  }

  /* ------------------------------------------------------- image lightbox */

  /**
   * Same-page zoom for the product gallery.
   *
   * The overlay markup is rendered by the section (`[data-mb-lightbox]` in
   * moonbow-main-product.liquid), so there is nothing to build at runtime
   * and nothing left behind when the Theme Editor swaps the section out.
   *
   * `getSlide` is a callback rather than an index because the gallery owns
   * the current slide; asking for it at click time means the two never drift.
   */
  function initZoom(gallery, api) {
    var section = gallery.closest('[data-mb-product]') || document;
    var box = $('[data-mb-lightbox]', section);
    var triggers = $$('[data-mb-zoom]', gallery);
    if (!box) return;

    var image = $('[data-mb-lightbox-img]', box);
    if (!image) return;

    var prevBtn = $('[data-mb-lightbox-prev]', box);
    var nextBtn = $('[data-mb-lightbox-next]', box);
    var counter = $('[data-mb-lightbox-count]', box);

    /*
      Every image in the gallery, in gallery order, read from the slides the
      section rendered (Shopify's own media). A video or 3D slide has no
      still to enlarge, so it is left out of the viewer; `map` keeps each
      entry's gallery index so closing the viewer lands the gallery on the
      same picture.
    */
    var items = [];
    api.slides.forEach(function (slide, galleryIndex) {
      var img = $('img[data-mb-zoom-src]', slide);
      if (!img) return;
      items.push({
        gallery: galleryIndex,
        src: img.getAttribute('data-mb-zoom-src'),
        srcset: img.getAttribute('data-mb-zoom-srcset'),
        width: img.getAttribute('data-mb-zoom-width'),
        height: img.getAttribute('data-mb-zoom-height'),
        alt: img.getAttribute('alt') || ''
      });
    });
    if (!items.length) return;

    var at = 0;
    var opener = null;

    /* The viewer entry for a gallery index (or the nearest image after it,
       when the gallery is on a video). */
    function itemFor(galleryIndex) {
      for (var i = 0; i < items.length; i++) {
        if (items[i].gallery >= galleryIndex) return i;
      }
      return 0;
    }

    function show(i) {
      at = ((i % items.length) + items.length) % items.length;
      var item = items[at];
      image.setAttribute('alt', item.alt);
      /* Intrinsic size up front, so the frame holds the right aspect ratio
         before the file lands and the picture never jumps or crops. */
      if (item.width) image.setAttribute('width', item.width);
      if (item.height) image.setAttribute('height', item.height);
      if (item.srcset) {
        image.setAttribute('srcset', item.srcset);
      } else {
        image.removeAttribute('srcset');
      }
      image.setAttribute('sizes', '100vw');
      image.src = item.src;
      if (counter) counter.textContent = at + 1 + ' / ' + items.length;
      var single = items.length < 2;
      if (prevBtn) prevBtn.hidden = single;
      if (nextBtn) nextBtn.hidden = single;
      if (counter) counter.hidden = single;
      /* Warm the neighbours so stepping through is instant. */
      [at + 1, at - 1].forEach(function (n) {
        var near = items[((n % items.length) + items.length) % items.length];
        if (near && near !== item) {
          var pre = new Image();
          pre.src = near.src;
        }
      });
    }

    function open() {
      opener = document.activeElement;
      show(itemFor(api.current()));

      box.hidden = false;
      /* Reading back a layout value flushes the `hidden` removal so the
         opening transition actually runs. */
      void box.offsetWidth;
      box.classList.add('is-open');
      scrollLock.lock();

      var closeBtn = $('button[data-mb-lightbox-close]', box);
      if (closeBtn) closeBtn.focus();
      triggers.forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'true');
      });
    }

    function close() {
      if (box.hidden) return;
      box.classList.remove('is-open');
      box.hidden = true;
      /* The gallery follows the viewer: it rests on the image the shopper
         stopped at, with its thumbnail marked. */
      if (items[at]) api.show(items[at].gallery);
      /* Drop the file so a large zoom is not held in memory once it is
         gone. `removeAttribute`, not `src = ''` â an empty src re-requests
         the current page URL in some browsers. */
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.removeAttribute('src');
      scrollLock.release();
      triggers.forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
      if (opener && opener.focus) opener.focus();
      opener = null;
    }

    triggers.forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', open);
    });

    /* Previous / next loop in their own direction: last -> first on next,
       first -> last on previous. */
    if (prevBtn) prevBtn.addEventListener('click', function () { show(at - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { show(at + 1); });

    /* The image itself opens the zoom too. On mobile the gallery is a
       scroll-snap carousel and a swipe that ends on the picture can still
       fire a click, so a recent scroll suppresses it. */
    var track = $('[data-mb-gallery-track]', gallery);
    var scrolledAt = 0;
    if (track) {
      track.addEventListener(
        'scroll',
        function () {
          scrolledAt = Date.now();
        },
        { passive: true }
      );
    }

    $$('[data-mb-gallery-slide] img[data-mb-zoom-src]', gallery).forEach(function (img) {
      img.classList.add('mb-gallery__zoomable');
      img.addEventListener('click', function () {
        if (Date.now() - scrolledAt < 350) return;
        open();
      });
    });

    $$('[data-mb-lightbox-close]', box).forEach(function (el) {
      el.addEventListener('click', close);
    });

    /* A horizontal swipe on a touch screen steps through the images. */
    var touch = null;
    box.addEventListener('touchstart', function (event) {
      if (event.touches.length !== 1) return;
      touch = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }, { passive: true });
    box.addEventListener('touchend', function (event) {
      if (!touch || items.length < 2) return;
      var t = event.changedTouches[0];
      var dx = t.clientX - touch.x;
      var dy = t.clientY - touch.y;
      touch = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) show(at + (dx < 0 ? 1 : -1));
    }, { passive: true });

    document.addEventListener('keydown', function (event) {
      if (box.hidden) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        show(at + 1);
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        show(at - 1);
        return;
      }
      if (event.key !== 'Escape') return;
      /* Stop here so Escape closes the zoom without also closing whatever
         is behind it. */
      event.stopPropagation();
      close();
    });

    /* Keep Tab inside the overlay while it is up: cycle through its own
       visible buttons. */
    box.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab') return;
      var focusable = $$('button', box).filter(function (el) {
        return !el.hidden && el.offsetParent !== null;
      });
      if (!focusable.length) return;
      event.preventDefault();
      var i = focusable.indexOf(document.activeElement);
      var step = event.shiftKey ? -1 : 1;
      focusable[(i + step + focusable.length) % focusable.length].focus();
    });
  }

  /* -------------------------------------------------------------- wishlist */

  var WISHLIST_KEY = 'moonbow_wishlist';

  /**
   * The saved wishlist - the one store every heart, the header count and the
   * wishlist drawer read and write.
   *
   * localStorage `moonbow_wishlist` holds an ordered list of
   * `{ id, handle }`: the product id is the stable key, the handle is what
   * the drawer needs to look the product up (`/products/<handle>.js`), so
   * titles, prices and images are always the store's current ones rather
   * than a copy frozen at save time. Earlier builds stored bare id strings;
   * those still count as saved (the heart stays lit) and pick up their
   * handle the next time their product page is visited.
   *
   * Every way client storage can fail is treated the same - an empty list:
   * no key yet, invalid JSON, a value that is not an array, or storage that
   * throws outright (private browsing, site data blocked). Nothing in here is
   * allowed to raise.
   */
  function readWishlistItems() {
    var raw;
    try {
      raw = window.localStorage.getItem(WISHLIST_KEY);
    } catch (e) {
      return [];
    }
    if (!raw) return [];

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return [];
    }
    if (!Array.isArray(parsed)) return [];

    var seen = {};
    var items = [];
    parsed.forEach(function (entry) {
      var id = typeof entry === 'string' ? entry : entry && typeof entry.id === 'string' ? entry.id : '';
      if (!id || seen[id]) return;
      seen[id] = true;
      var handle = entry && typeof entry.handle === 'string' ? entry.handle : '';
      items.push({ id: id, handle: handle });
    });
    return items;
  }

  /** The saved product ids, in saved order. */
  function readWishlist() {
    return readWishlistItems().map(function (item) {
      return item.id;
    });
  }

  function writeWishlistItems(items) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    } catch (e) {
      /* Storage full or unavailable. The in-page state still updated, it
         just will not survive the next page load. */
    }
  }

  /**
   * The one way the list changes: add, remove or toggle a product, then
   * repaint every control and tell the page. `handle` rides along on add.
   */
  function setWishlist(id, handle, saved) {
    var items = readWishlistItems();
    var at = -1;
    items.forEach(function (item, i) {
      if (item.id === id) at = i;
    });
    var next = typeof saved === 'boolean' ? saved : at === -1;
    if (next && at === -1) {
      items.push({ id: id, handle: handle || '' });
    } else if (next && at > -1 && handle && !items[at].handle) {
      items[at].handle = handle;
    } else if (!next && at > -1) {
      items.splice(at, 1);
    }
    writeWishlistItems(items);
    paintWishlist();
    document.dispatchEvent(
      new CustomEvent('mb:wishlist:change', {
        detail: {
          id: id,
          saved: next,
          ids: items.map(function (item) {
            return item.id;
          })
        }
      })
    );
  }

  function paintWishlistButton(btn, saved) {
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    btn.classList.toggle('is-active', saved);
    var label = saved
      ? btn.getAttribute('data-mb-wishlist-label-on') || 'Remove from wishlist'
      : btn.getAttribute('data-mb-wishlist-label-off') || 'Save for later';
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
  }

  /** Repaints every wishlist control and count on the page from storage. */
  function paintWishlist(root) {
    var ids = readWishlist();
    $$('[data-mb-wishlist]', root || document).forEach(function (btn) {
      var id = btn.getAttribute('data-mb-wishlist');
      if (!id) return;
      paintWishlistButton(btn, ids.indexOf(id) > -1);
    });
    $$('[data-mb-wishlist-count]').forEach(function (el) {
      el.textContent = ids.length ? String(ids.length) : '';
      el.hidden = ids.length === 0;
    });
    $$('[data-mb-wishlist-open]').forEach(function (btn) {
      btn.classList.toggle('is-active', ids.length > 0);
    });
  }

  /* ------------------------------------------------------- wishlist drawer */

  /*
    Product lookups for the drawer, kept for the page's lifetime: opening
    the drawer twice, or removing one item, never refetches the others.
  */
  var wishlistProducts = {};

  function fetchWishlistProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (wishlistProducts[handle]) return wishlistProducts[handle];
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    wishlistProducts[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });
    return wishlistProducts[handle];
  }

  /* The card's name rule: the store suffixes titles for SEO ("Reset ... |
     TCM Chrysanthemum Cleanser"), and the short name is what the theme
     shows everywhere else. */
  function shortTitle(title) {
    return String(title || '').split('|')[0].trim();
  }

  function sizedImage(src, width) {
    if (!src) return '';
    var url = src.indexOf('//') === 0 ? 'https:' + src : src;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function wishlistItemMarkup(item, product, removeLabel) {
    var li = document.createElement('li');
    li.className = 'mb-wl__item';
    li.setAttribute('data-mb-wishlist-item', item.id);

    var url = product.url || '/products/' + product.handle;
    var name = shortTitle(product.title);

    var media = document.createElement('a');
    media.className = 'mb-wl__media';
    media.href = url;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    var image = product.featured_image || (product.images && product.images[0]);
    if (image) {
      var img = document.createElement('img');
      img.src = sizedImage(image, 200);
      img.alt = '';
      img.width = 88;
      img.height = 88;
      img.loading = 'lazy';
      media.appendChild(img);
    }
    li.appendChild(media);

    var info = document.createElement('div');
    info.className = 'mb-wl__info';
    var link = document.createElement('a');
    link.className = 'mb-wl__name';
    link.href = url;
    link.textContent = name;
    info.appendChild(link);

    var price = document.createElement('p');
    price.className = 'mb-wl__price';
    var now = document.createElement('span');
    now.textContent = formatCardMoney(product.price);
    price.appendChild(now);
    if (product.compare_at_price && product.compare_at_price > product.price) {
      var was = document.createElement('s');
      was.textContent = formatCardMoney(product.compare_at_price);
      price.appendChild(was);
    }
    info.appendChild(price);
    li.appendChild(info);

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'mb-wl__remove';
    remove.setAttribute('data-mb-wishlist-remove', item.id);
    remove.setAttribute('aria-label', (removeLabel || 'Remove') + ' ' + name);
    remove.textContent = removeLabel || 'Remove';
    li.appendChild(remove);
    return li;
  }

  /** Fills every wishlist drawer on the page from the store. */
  function renderWishlistDrawers() {
    var drawers = $$('[data-mb-wishlist-drawer]');
    if (!drawers.length) return;
    var items = readWishlistItems();

    drawers.forEach(function (drawer) {
      var list = $('[data-mb-wishlist-list]', drawer);
      var empty = $('[data-mb-wishlist-empty]', drawer);
      if (!list) return;
      var removeLabel = drawer.getAttribute('data-remove-label') || 'Remove';
      var token = (drawer.__mbWishlistRender || 0) + 1;
      drawer.__mbWishlistRender = token;

      /* Items already on screen stay put; only what changed is added or
         dropped, so a removal is immediate and nothing flickers. */
      var wanted = {};
      items.forEach(function (item) {
        wanted[item.id] = true;
      });
      $$('[data-mb-wishlist-item]', list).forEach(function (el) {
        if (!wanted[el.getAttribute('data-mb-wishlist-item')]) el.remove();
      });

      var showable = items.filter(function (item) {
        return !!item.handle;
      });
      if (empty) empty.hidden = showable.length > 0;
      list.hidden = showable.length === 0;
      if (!showable.length) return;

      /* A product not on screen yet holds its place with a placeholder card
         while its details load, so the drawer is never blank and the order
         never shuffles. */
      showable.forEach(function (item) {
        if ($('[data-mb-wishlist-item="' + item.id + '"]', list)) return;
        var holder = document.createElement('li');
        holder.className = 'mb-wl__item is-loading';
        holder.setAttribute('data-mb-wishlist-item', item.id);
        holder.setAttribute('aria-busy', 'true');
        holder.innerHTML =
          '<span class="mb-wl__media"></span>' +
          '<span class="mb-wl__info"><span class="mb-wl__bar"></span><span class="mb-wl__bar mb-wl__bar--short"></span></span>';
        list.appendChild(holder);
      });

      Promise.all(
        showable.map(function (item) {
          return fetchWishlistProduct(item.handle);
        })
      ).then(function (products) {
        if (drawer.__mbWishlistRender !== token) return;
        var shown = 0;
        showable.forEach(function (item, i) {
          var product = products[i];
          var existing = $('[data-mb-wishlist-item="' + item.id + '"]', list);
          if (!product) {
            /* Gone from the store (deleted or unpublished): nothing to
               show, and nothing broken left in the list. */
            if (existing) existing.remove();
            return;
          }
          shown += 1;
          var el = existing && !existing.classList.contains('is-loading')
            ? existing
            : wishlistItemMarkup(item, product, removeLabel);
          if (existing && el !== existing) existing.remove();
          list.appendChild(el);
        });
        if (empty) empty.hidden = shown > 0;
        list.hidden = shown === 0;
      });
    });
  }

  /**
   * One wishlist for the whole site.
   *
   * Any control anywhere - the gallery's heart, a card, the drawer's remove
   * button - opts in with a data attribute and goes through `setWishlist`,
   * so every heart, the header count and the drawer always agree. Clicks are
   * delegated from the document, so controls rendered later need no
   * re-binding.
   */
  function initWishlist(root) {
    /* A saved product whose entry predates handles picks one up here. */
    $$('[data-mb-wishlist][data-mb-wishlist-handle]', root || document).forEach(function (btn) {
      var id = btn.getAttribute('data-mb-wishlist');
      var handle = btn.getAttribute('data-mb-wishlist-handle');
      var items = readWishlistItems();
      var needs = items.some(function (item) {
        return item.id === id && !item.handle;
      });
      if (needs) {
        items.forEach(function (item) {
          if (item.id === id) item.handle = handle;
        });
        writeWishlistItems(items);
      }
    });

    paintWishlist(root);
    renderWishlistDrawers();

    /* The listeners below are page-wide, so they are bound exactly once no
       matter how many sections initialise. */
    if (!once(document.documentElement, 'wishlist')) return;

    document.addEventListener('click', function (event) {
      var remove = event.target.closest ? event.target.closest('[data-mb-wishlist-remove]') : null;
      if (remove) {
        event.preventDefault();
        setWishlist(remove.getAttribute('data-mb-wishlist-remove'), '', false);
        return;
      }

      var btn = event.target.closest ? event.target.closest('[data-mb-wishlist]') : null;
      if (!btn) return;
      var id = btn.getAttribute('data-mb-wishlist');
      if (!id) return;
      event.preventDefault();
      setWishlist(id, btn.getAttribute('data-mb-wishlist-handle') || '');

      /* Saving shows the shopper where it went: the header heart's drawer. */
      var opener = $('[data-mb-wishlist-open]');
      if (opener && btn.getAttribute('aria-pressed') === 'true') opener.click();
    });

    document.addEventListener('mb:wishlist:change', renderWishlistDrawers);

    /* Another tab changed the list: follow it rather than showing a state
       the shopper has already undone somewhere else. */
    window.addEventListener('storage', function (event) {
      if (event.key && event.key !== WISHLIST_KEY) return;
      paintWishlist();
      renderWishlistDrawers();
    });

    /* Restored from the back/forward cache with a stale painted state. */
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      paintWishlist();
      renderWishlistDrawers();
    });
  }

  /* ------------------------------------------------------- accordion group */

  /*
    One open row at a time. Every accordion is its own theme-editor block, so
    the group is "the other accordions in the same product section": opening
    one closes whichever is open, and a second click on the open row closes
    it. All rows start closed, as the Figma draws them.
  */
  function setAccordion(item, open) {
    var trigger = $('[data-mb-accordion-trigger]', item);
    var panel = $('[data-mb-accordion-panel]', item);
    if (!trigger || !panel) return;
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.setAttribute('data-open', open ? 'true' : 'false');
  }

  function initAccordions(root) {
    $$('[data-mb-accordion]', root).forEach(function (item) {
      if (!once(item, 'accordion')) return;

      var trigger = $('[data-mb-accordion-trigger]', item);
      var panel = $('[data-mb-accordion-panel]', item);
      if (!trigger || !panel) return;

      trigger.addEventListener('click', function () {
        var open = trigger.getAttribute('aria-expanded') === 'true';
        if (!open) {
          var scope = item.closest('[data-mb-product]') || document;
          $$('[data-mb-accordion]', scope).forEach(function (other) {
            if (other !== item) setAccordion(other, false);
          });
        }
        setAccordion(item, !open);
      });
    });
  }

  /* --------------------------------------------------------- product form */

  function initProduct(root) {
    $$('[data-mb-product]', root).forEach(function (section) {
      if (!once(section, 'product')) return;

      var data = readJSON($('[data-mb-product-data]', section)) || {};
      var variants = data.variants || [];
      var form = $('[data-mb-product-form]', section);
      if (!form) return;

      /* Without pricing data we cannot improve on what Liquid already
         rendered, so leave the server output alone and only wire up the
         behaviour that does not depend on it. */
      var hasPricing = variants.length > 0;

      var variantInput = $('[data-mb-variant-input]', form);
      var planInput = $('[data-mb-selling-plan-input]', form);
      var qtyInput = $('[data-mb-qty-input]', section);
      var submit = $('[data-mb-atc]', form);
      var submitPrice = $('[data-mb-atc-price]', form);
      var submitText = $('[data-mb-atc-text]', form);
      var errorBox = $('[data-mb-form-error]', form);

      var priceCurrent = $('[data-mb-price-current]', section);
      var priceWas = $('[data-mb-price-was]', section);
      var priceSave = $('[data-mb-price-save]', section);

      var frequency = $('[data-mb-frequency]', section);
      var purchaseRadios = $$('[data-mb-purchase]', section);
      var detail = $('[data-mb-subscription-detail]', section);

      function currentVariant() {
        var id = variantInput ? Number(variantInput.value) : 0;
        for (var i = 0; i < variants.length; i++) {
          if (variants[i].id === id) return variants[i];
        }
        return variants[0] || null;
      }

      function selectedPlanId() {
        var mode = selectedMode();
        if (mode !== 'subscription') return '';
        return frequency ? frequency.value : '';
      }

      function selectedMode() {
        for (var i = 0; i < purchaseRadios.length; i++) {
          if (purchaseRadios[i].checked) return purchaseRadios[i].value;
        }
        return purchaseRadios.length ? purchaseRadios[0].value : 'onetime';
      }

      function findAllocation(variant, planId) {
        var allocations = (variant && variant.selling_plan_allocations) || [];
        for (var i = 0; i < allocations.length; i++) {
          if (String(allocations[i].selling_plan_id) === String(planId)) {
            return allocations[i];
          }
        }
        return null;
      }

      /** Price for the current variant under the current selling plan. */
      function resolvePrice() {
        var variant = currentVariant();
        if (!variant) return { price: 0, compare: null };

        var planId = selectedPlanId();
        if (planId) {
          var allocation = findAllocation(variant, planId);
          if (allocation) {
            return {
              price: allocation.price,
              compare:
                allocation.compare_at_price ||
                (allocation.price < variant.price ? variant.price : null)
            };
          }
        }
        return {
          price: variant.price,
          compare:
            variant.compare_at_price && variant.compare_at_price > variant.price
              ? variant.compare_at_price
              : null
        };
      }

      function render() {
        var variant = currentVariant();
        var resolved = resolvePrice();

        /* Whole amounts drop their cents ("$28", not "$28.00"), matching
           the Liquid's `money_without_trailing_zeros`. */
        if (priceCurrent) priceCurrent.textContent = formatCardMoney(resolved.price);

        if (priceWas) {
          if (resolved.compare) {
            priceWas.textContent = formatCardMoney(resolved.compare);
            priceWas.hidden = false;
          } else {
            priceWas.hidden = true;
          }
        }

        if (priceSave) {
          if (resolved.compare && resolved.compare > resolved.price) {
            var pct = Math.ceil(
              ((resolved.compare - resolved.price) * 100) / resolved.compare
            );
            priceSave.textContent = 'Save ' + pct + '%';
            priceSave.hidden = false;
          } else {
            priceSave.hidden = true;
          }
        }

        if (planInput) planInput.value = selectedPlanId();

        if (detail) detail.hidden = selectedMode() !== 'subscription';

        var available = variant ? variant.available : false;
        if (submit) {
          submit.disabled = !available;
          if (submitText) {
            submitText.textContent = available
              ? (window.theme &&
                  window.theme.strings &&
                  window.theme.strings.addToCart) ||
                'Add to cart'
              : (window.theme &&
                  window.theme.strings &&
                  window.theme.strings.soldOut) ||
                'Sold out';
          }
          if (submitPrice) {
            submitPrice.textContent = available
              ? '| ' + formatCardMoney(resolved.price)
              : '';
          }
        }

        /* The free-shipping meter counts this item at its shown price. */
        var qtyNow = qtyInput ? Math.max(1, Number(qtyInput.value) || 1) : 1;
        section.dispatchEvent(
          new CustomEvent('mb:product:line', {
            detail: { price: resolved.price * qtyNow, subscription: selectedMode() === 'subscription' && !!selectedPlanId() }
          })
        );

        /* Keep the per-option prices in step with the variant. The
           subscribe row always quotes whichever frequency is chosen, even
           while one-time is the selected purchase mode. */
        $$('[data-mb-option-price]', section).forEach(function (el) {
          var planId = el.getAttribute('data-mb-option-price');
          if (planId === 'subscription') planId = frequency ? frequency.value : '';

          var v = currentVariant();
          if (!v) return;

          var allocation = planId ? findAllocation(v, planId) : null;
          el.textContent = formatCardMoney(allocation ? allocation.price : v.price);

          if (planId) {
            var pct = allocation && v.price > allocation.price
              ? Math.round(((v.price - allocation.price) / v.price) * 100)
              : 0;
            $$('[data-mb-subscribe-percent]', section).forEach(function (label) {
              label.textContent = pct > 0 ? pct + '%' : '';
            });
            /* The chosen frequency's own wording, built in Liquid from that
               plan's Recharge price adjustments - never recomputed here. */
            var chosen = frequency && frequency.options[frequency.selectedIndex];
            if (chosen) {
              $$('[data-mb-subscribe-summary]', section).forEach(function (label) {
                label.textContent = chosen.getAttribute('data-summary') || '';
              });
            }
          }
        });

        $$('[data-mb-option-was]', section).forEach(function (el) {
          var v = currentVariant();
          if (v) el.textContent = formatCardMoney(v.price);
        });
      }

      function onSelectionChange() {
        if (planInput) planInput.value = selectedPlanId();
        if (detail) detail.hidden = selectedMode() !== 'subscription';
        if (hasPricing) render();
      }

      purchaseRadios.forEach(function (radio) {
        radio.addEventListener('change', onSelectionChange);
      });
      if (frequency) frequency.addEventListener('change', onSelectionChange);

      /* Variant selectors (only rendered when the product has real options) */
      $$('[data-mb-variant-select]', section).forEach(function (select) {
        select.addEventListener('change', function () {
          if (variantInput) variantInput.value = select.value;
          if (hasPricing) render();
          var url = new URL(window.location.href);
          url.searchParams.set('variant', select.value);
          window.history.replaceState({}, '', url.toString());
        });
      });

      initOptionPicker(section, variants, currentVariant);

      /* Quantity */
      $$('[data-mb-qty-step]', section).forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (!qtyInput) return;
          var step = Number(btn.getAttribute('data-mb-qty-step')) || 1;
          var value = Math.max(1, (Number(qtyInput.value) || 1) + step);
          qtyInput.value = value;
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      if (qtyInput) {
        qtyInput.addEventListener('change', function () {
          var value = Math.max(1, Math.floor(Number(qtyInput.value) || 1));
          qtyInput.value = value;
          var minus = $('[data-mb-qty-step="-1"]', section);
          if (minus) minus.disabled = value <= 1;
          if (hasPricing && section.__mbShipReady) render();
        });
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* Submit */
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        if (!submit || submit.disabled) return;

        if (errorBox) errorBox.textContent = '';
        submit.classList.add('is-loading');

        var payload = {
          items: [
            {
              id: Number(variantInput.value),
              quantity: Number(qtyInput ? qtyInput.value : 1) || 1
            }
          ]
        };
        var plan = selectedPlanId();
        if (plan) payload.items[0].selling_plan = Number(plan);

        addToCart(payload)
          .then(function (result) {
            return announceCartAdd(result);
          })
          .catch(function (error) {
            if (errorBox) errorBox.textContent = error.message;
          })
          .then(function () {
            submit.classList.remove('is-loading');
          });
      });

      initShipBar(section);
      section.__mbShipReady = true;
      if (hasPricing) render();
    });
  }

  /* --------------------------------------------------------- option picker */

  /*
    Colour swatches + one dropdown per remaining option, rendered instead of
    the combined variant list when the product has a Color option. These only
    pick option VALUES: the variant they add up to is written to the hidden
    combined `[data-mb-variant-select]` and its `change` fired, so price,
    stock, the URL and the cart id all keep the one path `initProduct`
    already has.

    Choosing a colour keeps it and, if the exact combination is sold out or
    missing, moves the other options to the first available variant in that
    colour (a colour whose only stock is L/XL lands on L/XL). The variant's own
    image, when it has one, becomes the gallery's main image.
  */
  function initOptionPicker(section, variants, currentVariant) {
    var controls = $$('[data-mb-option]', section);
    var combined = $('[data-mb-variant-select]', section);
    if (!controls.length || !combined || !variants.length) return;

    var soldOut =
      (window.theme && window.theme.strings && window.theme.strings.soldOut) || 'Sold out';

    function position(control) {
      return Number(control.getAttribute('data-mb-option')) - 1;
    }

    function chosen() {
      var values = [];
      controls.forEach(function (control) {
        if (control.type === 'radio' && !control.checked) return;
        values[position(control)] = control.value;
      });
      return values;
    }

    function fits(variant, values) {
      for (var i = 0; i < values.length; i++) {
        if (values[i] != null && variant.options[i] !== values[i]) return false;
      }
      return true;
    }

    function find(test) {
      for (var i = 0; i < variants.length; i++) {
        if (test(variants[i])) return variants[i];
      }
      return null;
    }

    /* Only a colour change moves the other options; changing size on a
       sold-out colour stays on that colour and shows it sold out. */
    function resolve(changedAt, isColour) {
      var values = chosen();
      var exact = find(function (v) { return fits(v, values); });
      if (exact && (exact.available || !isColour)) return exact;
      var keep = values[changedAt];
      return (
        find(function (v) { return v.available && v.options[changedAt] === keep; }) ||
        exact ||
        find(function (v) { return v.options[changedAt] === keep; })
      );
    }

    /* A value is "available" when some in-stock variant has it together
       with the other options as they stand now. */
    function inStock(values, at, value) {
      var test = values.slice();
      test[at] = value;
      return !!find(function (v) { return v.available && fits(v, test); });
    }

    function sync(variant) {
      var values = variant.options;
      controls.forEach(function (control) {
        var at = position(control);
        if (control.type === 'radio') {
          control.checked = control.value === values[at];
          var swatch = control.closest('.mb-swatch, .mb-optpill');
          if (swatch) swatch.classList.toggle('is-unavailable', !inStock(values, at, control.value));
        } else {
          control.value = values[at];
          Array.prototype.forEach.call(control.options, function (option) {
            if (!option.hasAttribute('data-label')) option.setAttribute('data-label', option.textContent.trim());
            option.textContent =
              option.getAttribute('data-label') +
              (inStock(values, at, option.value) ? '' : ' - ' + soldOut);
          });
        }
      });
      $$('[data-mb-option-value]', section).forEach(function (label) {
        var checked = $('input:checked', label.closest('fieldset'));
        label.textContent = checked ? checked.value : '';
      });
    }

    function showImage(variant) {
      if (!variant || !variant.featured_media_id) return;
      var gallery = $('[data-mb-gallery]', section);
      if (!gallery) return;
      gallery.dispatchEvent(
        new CustomEvent('mb:gallery:show-media', { detail: { mediaId: variant.featured_media_id } })
      );
    }

    controls.forEach(function (control) {
      control.addEventListener('change', function () {
        var variant = resolve(position(control), !!control.closest('.mb-swatch'));
        if (!variant) return;
        sync(variant);
        if (combined.value !== String(variant.id)) {
          combined.value = String(variant.id);
          combined.dispatchEvent(new Event('change', { bubbles: true }));
        }
        showImage(variant);
      });
    });

    var start = currentVariant();
    if (start) {
      sync(start);
      /* 2026-10-21 (Iram): the page opens on the selected variant's own image
         (the chosen colour), not the product's first photo. Deferred so the
         gallery has bound its listener. */
      setTimeout(function () { showImage(start); }, 0);
    }
  }

  /* ---------------------------------------------------------------- upsell */

  /*
    Quick-add buttons. `data-mb-upsell-add` is used by the product column's
    "You May Also like" AND by the testimonials section's product card, which
    is a separate Shopify section - so this runs over the whole scope rather
    than from inside `initProduct`, where it only ever reached the two buttons
    inside `.mb-product` and left the ten in the carousel doing nothing.

    `once` is per button, so a section re-render binds only what is new.
  */
  function initUpsell(root) {
    $$('[data-mb-upsell-add]', root).forEach(function (btn) {
      if (!once(btn, 'upsell')) return;
      btn.addEventListener('click', function () {
        var id = Number(btn.getAttribute('data-mb-upsell-add'));
        if (!id || btn.disabled) return;
        btn.classList.add('is-loading');
        addToCart({ items: [{ id: id, quantity: 1 }] })
          .then(announceCartAdd)
          .catch(function () {})
          .then(function () {
            btn.classList.remove('is-loading');
          });
      });
    });
  }

  /* ------------------------------------------------------- routine + bundle */

  /**
   * "Complete the routine".
   *
   * Two independent controls: the per-card add button (one product, same
   * contract as the upsell quick-add) and the bundle bar, where dropping a
   * tile re-prices the offer live and the add button sends whatever is still
   * selected to the cart in one request.
   *
   * The bundle's discount is a *display* of a discount the merchant has to
   * configure in Shopify (an automatic discount or a Function) â a theme
   * cannot change what the cart charges. The struck-through total and the
   * "Save x%" pill therefore describe the offer; the cart applies it.
   */
  function initRoutine(root) {
    $$('[data-mb-routine]', root).forEach(function (section) {
      if (!once(section, 'routine')) return;

      /* Single-product adds. */
      $$('[data-mb-routine-add]', section).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var id = Number(btn.getAttribute('data-mb-routine-add'));
          if (!id || btn.disabled) return;
          btn.classList.add('is-loading');
          addToCart({ items: [{ id: id, quantity: 1 }] })
            .then(announceCartAdd)
            .catch(function () {})
            .then(function () {
              btn.classList.remove('is-loading');
            });
        });
      });

      var bundle = $('[data-mb-bundle]', section);
      if (!bundle) return;

      var discount = Number(section.getAttribute('data-mb-routine-discount')) || 0;
      var base = $('[data-mb-bundle-base]', bundle);
      var toggles = $$('[data-mb-bundle-toggle]', bundle);
      var wasEl = $('[data-mb-bundle-was]', bundle);
      var nowEl = $('[data-mb-bundle-now]', bundle);
      var addBtn = $('[data-mb-bundle-add]', bundle);
      var saveEl = $('[data-mb-bundle-save]', bundle) || $('.mb-bundle__save', bundle);
      var incentiveEl = $('[data-mb-bundle-incentive]', bundle);

      function selection() {
        var items = [];
        if (base) {
          items.push({
            id: Number(base.getAttribute('data-mb-bundle-base')),
            price: Number(base.getAttribute('data-mb-bundle-price')) || 0
          });
        }
        toggles.forEach(function (input) {
          if (!input.checked) return;
          items.push({
            id: Number(input.getAttribute('data-mb-bundle-toggle')),
            price: Number(input.getAttribute('data-mb-bundle-price')) || 0
          });
        });
        return items;
      }

      function paint() {
        var items = selection();
        var full = items.reduce(function (sum, item) {
          return sum + item.price;
        }, 0);
        /* W5 2026-10-15 (Iram): the saving needs 2+ products (the main one
           plus at least one add-on). On its own the main product shows its
           normal price - no struck total, no Save badge - and the incentive
           line asks for more products. */
        var deal = items.length >= 2 && discount > 0;
        /* Round the same way Liquid does, so the first paint never moves. */
        var off = deal ? Math.round((full * discount) / 100) : 0;

        /* Trimmed like the Liquid (`money_without_trailing_zeros`). */
        if (wasEl) {
          wasEl.textContent = formatCardMoney(full);
          wasEl.hidden = !deal;
        }
        if (nowEl) nowEl.textContent = formatCardMoney(full - off);
        if (saveEl) saveEl.hidden = !deal;
        if (incentiveEl) {
          incentiveEl.textContent = incentiveEl.getAttribute(deal ? 'data-saving' : 'data-more') || '';
        }
        if (addBtn) addBtn.disabled = items.length === 0;
      }

      toggles.forEach(function (input) {
        input.addEventListener('change', paint);
      });

      /* W5c 2026-10-15 (Iram): each add-on shot fills its design-sized card.
         Shown whole at full card height (contain) on its own background
         colour (the shot's top-left pixel), so the card reads as filled with
         no bands; a shot wider than its card gets cover (.is-wide). */
      /* 2026-10-21: the main product box works the same way. */
      var tileImgs = $$('.mb-bundle__tile img, .mb-bundle__base img', bundle);
      function fitTile(img) {
        var tile = img.parentNode;
        if (!img.naturalWidth || !tile || !tile.clientHeight) return;
        tile.classList.toggle('is-wide', img.naturalWidth / img.naturalHeight > tile.clientWidth / tile.clientHeight);
        if (tile.getAttribute('data-mb-tile-bg')) return;
        try {
          var c = document.createElement('canvas');
          c.width = c.height = 1;
          var ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0, 1, 1, 0, 0, 1, 1);
          var p = ctx.getImageData(0, 0, 1, 1).data;
          var bg = 'rgb(' + p[0] + ', ' + p[1] + ', ' + p[2] + ')';
          tile.style.backgroundColor = bg;
          tile.setAttribute('data-mb-tile-bg', bg);
        } catch (e) {}
      }
      tileImgs.forEach(function (img) {
        if (img.complete) fitTile(img);
        img.addEventListener('load', function () { fitTile(img); });
      });
      window.addEventListener('resize', function () {
        tileImgs.forEach(fitTile);
      });

      if (addBtn) {
        addBtn.addEventListener('click', function () {
          var items = selection().map(function (item) {
            return { id: item.id, quantity: 1 };
          });
          if (!items.length) return;
          addBtn.classList.add('is-loading');
          addToCart({ items: items })
            .then(announceCartAdd)
            .catch(function () {})
            .then(function () {
              addBtn.classList.remove('is-loading');
            });
        });
      }

      paint();
    });
  }

  /* -------------------------------------------------------- shipping meter */

  function initShipBar(section) {
    var bar = $('[data-mb-shipbar]', section);
    if (!bar || !once(bar, 'shipbar')) return;

    var threshold = Number(bar.getAttribute('data-threshold')) || 0;
    var fill = $('[data-mb-shipbar-fill]', bar);
    var itemFill = $('[data-mb-shipbar-item]', bar);
    var keyCart = $('[data-mb-key-cart]', bar);
    var keyCartAmount = $('[data-mb-key-cart-amount]', bar);
    var keyItem = $('[data-mb-key-item]', bar);
    var title = $('[data-mb-shipbar-title]', bar);
    var progressLabel = $('[data-mb-shipbar-progress]', bar);
    var stateLabel = $('[data-mb-shipbar-state]', bar);
    var titleTemplate = title ? title.getAttribute('data-template') || '' : '';
    var unlockedText = bar.getAttribute('data-unlocked') || '';
    var lockedText = bar.getAttribute('data-locked') || '';
    var reachedText = bar.getAttribute('data-reached') || '';
    var unlockText = bar.getAttribute('data-unlock') || reachedText;
    var subscriptionText = bar.getAttribute('data-subscription') || reachedText;

    /* 2026-10-21 (Iram): progress = cart + this item (price x quantity under
       the chosen purchase option); a subscription always ships free. */
    var cartTotal = Number(bar.getAttribute('data-cart-total')) || 0;
    var line = { price: 0, subscription: false, known: false };

    function paint() {
      if (!threshold) return;
      var total = cartTotal + (line.known ? line.price : 0);
      var remaining = line.subscription ? 0 : Math.max(0, threshold - total);
      var pct = line.subscription ? 100 : Math.min(100, Math.round((total / threshold) * 100));

      bar.classList.toggle('is-unlocked', remaining === 0);
      if (fill) fill.style.width = Math.min(100, Math.round((cartTotal / threshold) * 100)) + '%';
      if (itemFill) itemFill.style.width = pct + '%';
      if (progressLabel) {
        progressLabel.textContent = line.subscription
          ? ''
          : 'of ' + formatCardMoney(threshold);
      }
      /* The key under the bar: dark = in the cart, light = this item. */
      if (keyCart) {
        keyCart.hidden = cartTotal <= 0;
        if (keyCartAmount) keyCartAmount.textContent = formatCardMoney(cartTotal);
      }
      if (keyItem && line.known) {
        keyItem.textContent = line.subscription
          ? keyItem.getAttribute('data-free') || ''
          : (keyItem.getAttribute('data-label') || '') + ' ' + formatCardMoney(cartTotal + line.price);
      }
      if (progressLabel && remaining === 0) progressLabel.textContent = '';
      if (stateLabel) {
        stateLabel.textContent = remaining > 0 ? '' : unlockedText;
      }
      if (title && titleTemplate) {
        title.textContent = line.subscription
          ? subscriptionText
          : remaining > 0
            ? titleTemplate.replace('[amount]', formatCardMoney(remaining))
            : cartTotal >= threshold
              ? reachedText
              : unlockText;
      }
    }

    section.addEventListener('mb:product:line', function (event) {
      line.price = Number(event.detail && event.detail.price) || 0;
      line.subscription = !!(event.detail && event.detail.subscription);
      line.known = true;
      paint();
    });

    function refresh() {
      fetch(routes().cart_url ? routes().cart_url + '.js' : '/cart.js')
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (cart) {
          if (!cart) return;
          cartTotal = cart.total_price;
          paint();
        })
        .catch(function () {});
    }

    /* The server already rendered the correct copy for the current cart â
       only repaint once the cart actually changes. */
    document.body.addEventListener('shapes:cart:afteradditem', refresh);
    document.addEventListener('shapes:cart:update', refresh);
    document.addEventListener('shapes:cart:cartqtychange', refresh);
    /* A page restored from the back/forward cache carries an old cart. */
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) refresh();
    });
  }


  /* ------------------------------------------------------------------ faq */

  /*
    "Load more" on a FAQ that opts in (the collection's, not the product
    page's - the setting is off there, so no button is rendered and none of
    this runs). Rows past the opening count are in the markup already,
    `hidden`; each press reveals one more row in each column, and the button
    goes once nothing is left. Revealed rows are the same markup `initFaq`
    already bound, so the accordions keep working.
  */
  function initFaqMore(root) {
    $$('[data-mb-faq-more]', root).forEach(function (button) {
      if (!once(button, 'faqmore')) return;
      var section = button.closest('[data-mb-faq]') || button.parentElement.parentElement;
      var step = Number(button.getAttribute('data-step')) || 3;
      var columns = $$('.mb-faq__col', section).length || 1;

      /*
        Only the active tab's held rows count: releasing a row from the
        other group would change nothing on screen, and the button has to
        go when *this* tab has nothing left. `initFaq` publishes the tab as
        `data-mb-faq-group` (empty when there are no tabs).
      */
      function held() {
        var group = section.getAttribute('data-mb-faq-group') || '';
        return $$('[data-mb-faq-extra]', section).filter(function (item) {
          return !group || (item.getAttribute('data-group') || 'product') === group;
        });
      }

      function sync() {
        button.hidden = held().length === 0;
      }

      button.addEventListener('click', function () {
        /* One more row per column; `initFaq` re-flows the columns. */
        held().slice(0, step * columns).forEach(function (item) {
          item.removeAttribute('data-mb-faq-extra');
        });
        section.dispatchEvent(new CustomEvent('mb:faq:refresh'));
        sync();
      });

      section.addEventListener('mb:faq:group', sync);
      sync();
    });
  }

  function initFaq(root) {
    $$('[data-mb-faq]', root).forEach(function (section) {
      if (!once(section, 'faq')) return;

      /*
        One answer open at a time across the whole section - the two columns
        are one accordion, not two. Opening a question closes whichever one
        is open, in either column; clicking the open one closes it.
      */
      var faqItems = $$('[data-mb-faq-item]', section);
      faqItems.forEach(function (item) {
        var trigger = $('[data-mb-faq-trigger]', item);
        var panel = $('[data-mb-faq-panel]', item);
        if (!trigger || !panel) return;

        trigger.addEventListener('click', function () {
          var open = trigger.getAttribute('aria-expanded') === 'true';
          if (!open) {
            faqItems.forEach(function (other) {
              if (other === item) return;
              var t = $('[data-mb-faq-trigger]', other);
              var p = $('[data-mb-faq-panel]', other);
              if (t) t.setAttribute('aria-expanded', 'false');
              if (p) p.setAttribute('data-open', 'false');
            });
          }
          trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
          panel.setAttribute('data-open', open ? 'false' : 'true');
        });
      });

      /*
        Every question of the active tab is listed - there is no "Load more".
        The tabs only decide which group is on screen.
      */
      var tabs = $$('[data-mb-faq-tab]', section);
      var items = $$('[data-mb-faq-item]', section);
      var cols = $$('.mb-faq__col', section);
      var group = tabs.length ? 'product' : null;
      section.setAttribute('data-mb-faq-group', group || '');
      var openFirst = section.getAttribute('data-open-first') === 'true';

      function setOpen(item, open) {
        var trigger = $('[data-mb-faq-trigger]', item);
        var panel = $('[data-mb-faq-panel]', item);
        if (!trigger || !panel) return;
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.setAttribute('data-open', open ? 'true' : 'false');
      }

      /*
        The Figma fills the two columns 3 / 4, not alternately: with the first
        question expanded (147 tall against a closed 65) that is what makes
        the columns end level. Liquid splits the *whole* set at floor(n / 2),
        but only the active tab's questions are on screen, so the split has to
        be redone here over what is actually visible.

        A column is only rebuilt when its visible children have actually
        changed, so a tab switch does not churn the DOM - and
        an open panel is not re-appended, which would drop its transition.
        Moving a node keeps its listeners, so nothing is re-bound.
      */
      function distribute(visible) {
        if (cols.length !== 2) return;
        var split = Math.floor(visible.length / 2);
        [visible.slice(0, split), visible.slice(split)].forEach(function (want, i) {
          var col = cols[i];
          var have = [].filter.call(col.children, function (el) {
            return !el.hidden;
          });
          var same =
            have.length === want.length &&
            have.every(function (el, n) {
              return el === want[n];
            });
          if (same) return;
          want.forEach(function (el) {
            col.appendChild(el);
          });
        });
      }

      function apply() {
        var visible = [];

        items.forEach(function (item) {
          var inGroup = !group || (item.getAttribute('data-group') || 'product') === group;
          /* A row the "load more" button has not released yet stays off,
             whichever tab it belongs to. */
          var held = item.hasAttribute('data-mb-faq-extra');
          item.hidden = !inGroup || held;
          if (inGroup && !held) visible.push(item);
        });

        distribute(visible);

        /*
          Both Figma frames draw the first question expanded, so a tab always
          has one answer showing. This opens it by setting the same two
          attributes a click would - the state stays real, and the panel
          animates through the same grid-rows transition - and it only fires
          when the visible set has nothing open, so it never fights a shopper
          who has closed it.
        */
        if (!openFirst || !visible.length) return;
        var anyOpen = visible.some(function (item) {
          var trigger = $('[data-mb-faq-trigger]', item);
          return trigger && trigger.getAttribute('aria-expanded') === 'true';
        });
        if (!anyOpen) setOpen(visible[0], true);
      }

      section.addEventListener('mb:faq:refresh', apply);

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          group = tab.getAttribute('data-mb-faq-tab');
          tabs.forEach(function (other) {
            other.setAttribute('aria-selected', other === tab ? 'true' : 'false');
          });
          /* The single open answer never survives in the tab being left. */
          items.forEach(function (item) {
            setOpen(item, false);
          });
          apply();
          section.setAttribute('data-mb-faq-group', group);
          section.dispatchEvent(new CustomEvent('mb:faq:group'));
        });
      });

      apply();
    });
  }

  /* --------------------------------------------------- image comparison */

  /**
   * Before/after comparison. One custom property (`--mb-compare`) drives the
   * clipped layer, the divider and the knob, so the two images can never
   * drift apart. Left/right at every width - both current Figma frames draw
   * a vertical divider with a â¹ âº knob.
   */
  function initCompare(root) {
    $$('[data-mb-compare]', root).forEach(function (box) {
      if (!once(box, 'compare')) return;

      var handle = $('[data-mb-compare-handle]', box);
      if (!handle) return;

      var start = parseFloat(box.getAttribute('data-mb-compare-start'));
      var value = isNaN(start) ? 50 : start;

      function clamp(n) {
        return Math.max(0, Math.min(100, n));
      }

      function apply(next, animate) {
        value = clamp(next);
        box.classList.toggle('is-dragging', !animate);
        box.style.setProperty('--mb-compare', value + '%');
        handle.setAttribute('aria-valuenow', Math.round(value));
      }

      function fromPointer(event) {
        var rect = box.getBoundingClientRect();
        if (!rect.width) return;
        apply(((event.clientX - rect.left) / rect.width) * 100, false);
      }

      var dragging = false;

      box.addEventListener('pointerdown', function (event) {
        if (event.button && event.button !== 0) return;
        dragging = true;
        fromPointer(event);
        // Capture keeps the drag alive outside the circle. It can throw for
        // a pointer the browser no longer considers active, which must not
        // stop the position from having been set.
        try {
          box.setPointerCapture(event.pointerId);
        } catch (e) {
          /* not capturable â the document-level move still tracks it */
        }
        event.preventDefault();
      });

      box.addEventListener('pointermove', function (event) {
        if (!dragging) return;
        fromPointer(event);
      });

      function end(event) {
        if (!dragging) return;
        dragging = false;
        box.classList.remove('is-dragging');
        try {
          if (box.hasPointerCapture && box.hasPointerCapture(event.pointerId)) {
            box.releasePointerCapture(event.pointerId);
          }
        } catch (e) {
          /* already released */
        }
      }

      box.addEventListener('pointerup', end);
      box.addEventListener('pointercancel', end);

      handle.addEventListener('keydown', function (event) {
        var step = event.shiftKey ? 10 : 2;
        var next = null;
        switch (event.key) {
          case 'ArrowLeft':
          case 'ArrowUp':
            next = value - step;
            break;
          case 'ArrowRight':
          case 'ArrowDown':
            next = value + step;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = 100;
            break;
          default:
            return;
        }
        event.preventDefault();
        apply(next, true);
      });

      // The knob is inside the box, so its own pointerdown would double up.
      handle.addEventListener('click', function (event) {
        event.preventDefault();
      });

      apply(value, true);
    });
  }

  /* ------------------------------------------------------- ingredients */

  /**
   * One ingredient row open at a time. A row is an East cell and a West cell
   * sharing `data-mb-ing-row`; clicking either opens both - their
   * descriptions show - closes the row that was open, and swaps the circle
   * to the row's own before/after pair, or the shared pair when the row has
   * none. The open row stays open: there is always one pair on show.
   */
  function initIngredients(root) {
    $$('[data-mb-ingredients]', root).forEach(function (section) {
      if (!once(section, 'ingredients')) return;

      var cells = $$('[data-mb-ing-row]', section);
      var pairs = $$('[data-mb-ing-pair]', section);

      function open(row) {
        cells.forEach(function (cell) {
          var on = cell.getAttribute('data-mb-ing-row') === row;
          cell.classList.toggle('is-active', on);
          var trigger = $('[data-mb-ing-trigger]', cell);
          if (trigger) trigger.setAttribute('aria-expanded', on ? 'true' : 'false');
        });

        var own = pairs.filter(function (pair) {
          return pair.getAttribute('data-mb-ing-pair') === row;
        })[0];
        var shown = own || pairs.filter(function (pair) {
          return pair.getAttribute('data-mb-ing-pair') === 'shared';
        })[0];
        if (!shown) return;
        pairs.forEach(function (pair) {
          pair.hidden = pair !== shown;
        });
      }

      /* One active row for the whole section - the only state. Clicking a
         name and scrolling both go through `activate`. */
      var current = null;
      cells.forEach(function (cell) {
        if (cell.classList.contains('is-active')) current = cell.getAttribute('data-mb-ing-row');
      });

      function activate(row) {
        if (!row || row === current) return;
        current = row;
        open(row);
      }

      $$('[data-mb-ing-trigger]', section).forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var cell = trigger.closest('[data-mb-ing-row]');
          if (cell) scrollActivate(cell.getAttribute('data-mb-ing-row'));
        });
      });

      var rows = [];
      cells.forEach(function (cell) {
        var row = cell.getAttribute('data-mb-ing-row');
        if (rows.indexOf(row) === -1) rows.push(row);
      });
      if (rows.length < 2) return;

      /* The fresh row's copy and picture ease in; the grid-rows panel and
         the pair cross-fade in CSS carry the rest. */
      function reveal(row) {
        if (!window.gsap) return;
        cells.forEach(function (cell) {
          if (cell.getAttribute('data-mb-ing-row') !== row) return;
          var desc = $('.mb-ing__desc', cell);
          if (desc) {
            window.gsap.fromTo(
              desc,
              { autoAlpha: 0, y: 8 },
              { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out', overwrite: true }
            );
          }
        });
      }

      /* The single entry point for both clicks and scroll. */
      function scrollActivate(row) {
        if (!row || row === current) return;
        activate(row);
        reveal(row);
      }

      /*
        Scroll-driven reveal, on GSAP ScrollTrigger (loaded by the section).

        When the held block fits under the sticky header it pins, centred in
        the space below the header, and the scroll it holds for is divided
        into one step per row - 1 -> 2 -> 3 ... going down, the same steps in
        reverse going up, because the row is always read from the trigger's
        progress, never stored as "the next one". Desktop holds the section;
        phones hold the columns and circle, with a longer step.

        When it does not fit: no pin. The steps are spread over the
        section's own pass through the viewport, so each row still gets a
        stretch of scroll. Tapping a row works throughout.

        `gsap.matchMedia` builds the right setup per breakpoint and reverts
        it when the breakpoint changes; the instance is kept on the section
        and reverted on a theme-editor unload, so a re-rendered section never
        ends up with two triggers driving the same rows.
      */
      function rowAt(progress) {
        var i = Math.min(rows.length - 1, Math.floor(progress * rows.length));
        return rows[Math.max(0, i)];
      }

      function headerBottom() {
        var bar = document.querySelector('.shopify-section:has(> .mb-header-wrap--sticky)');
        if (!bar) return 0;
        var top = parseFloat(getComputedStyle(bar).top) || 0;
        return top + bar.offsetHeight;
      }

      /*
        moonbow.js runs before the section's own deferred GSAP scripts, so
        when GSAP is not here yet the setup waits for DOMContentLoaded -
        which only fires once every deferred script has run - and decides
        then between ScrollTrigger and the fallback.
      */
      var setUp = false;
      function setUpScroll() {
        if (setUp) return;
        setUp = true;
        if (window.gsap && window.ScrollTrigger) {
          setUpScrollTrigger(window.gsap, window.ScrollTrigger);
        } else {
          setUpObserver();
        }
      }
      if (window.gsap && window.ScrollTrigger) {
        setUpScroll();
      } else if (document.readyState !== 'complete') {
        document.addEventListener('DOMContentLoaded', setUpScroll, { once: true });
        window.addEventListener('load', setUpScroll, { once: true });
      } else {
        setUpScroll();
      }

      function setUpScrollTrigger(gsap, ScrollTrigger) {
        gsap.registerPlugin(ScrollTrigger);
        /* A phone's address bar showing and hiding resizes the viewport on
           every scroll direction change; re-measuring then would nudge the
           pinned block. */
        ScrollTrigger.config({ ignoreMobileResize: true });
        if (section.__mbIngMM) section.__mbIngMM.revert();
        var mm = gsap.matchMedia();
        section.__mbIngMM = mm;

        mm.add(
          {
            /* One of these always matches: matchMedia only runs the setup
               while at least one condition is true. */
            desktop: '(min-width: 1024px)',
            phone: '(max-width: 1023px)',
            reduce: '(prefers-reduced-motion: reduce)'
          },
          function (context) {
            var desktop = context.conditions.desktop;
            var reduce = context.conditions.reduce;

            /*
              What stays in view while the rows step. Desktop holds the whole
              section. Phones hold the two ingredient columns and the
              before/after circle under them (`.mb-ing__grid`) - the heading
              has been read by then and scrolls away normally - so the open
              ingredient and its pictures stay put while it is read.

              It is only held when it fits the space under the sticky header;
              otherwise (a very short phone, or reduced motion) nothing pins
              and the steps are spread over the section's own pass. Either
              way the page scrolls natively - a pin holds this block in
              place, it never takes over the scroll.
            */
            var grid = $('.mb-ing__grid', section);
            var held = desktop ? section : grid || section;
            var room = function () {
              return window.innerHeight - headerBottom() - (desktop ? 0 : 16);
            };
            var pin = !reduce && held.offsetHeight <= room();

            /* Scroll per ingredient: 30% of the screen on desktop; 45% on a
               phone, where the text is smaller and needs longer on screen. */
            var step = function () {
              return Math.round(window.innerHeight * (desktop ? 0.3 : 0.45));
            };

            if (!desktop) section.setAttribute('data-mb-ing-scroll', '');

            /*
              The hold is CSS `position: sticky`, not a ScrollTrigger pin. A
              pin switches the block to `position: fixed` from script as the
              hold begins and ends, and on a phone the momentum scroll runs
              ahead of script, so that switch landed a frame late and the
              block jerked. Sticky is resolved by the browser's own scroller,
              frame for frame, in both directions.

              A spacer after the held block supplies the scroll the hold
              lasts for (one step per row after the first); while it passes,
              the block stays stuck, then scrolls on with the page - nothing
              is left behind, because the stuck block covers the spacer.
              ScrollTrigger only reads the progress through that stretch.
            */
            var spacer = null;
            var st;
            var holdTop = function () {
              var gap = Math.max(desktop ? 0 : 8, (room() - held.offsetHeight) / 2);
              return Math.round(headerBottom() + gap);
            };
            if (pin) {
              spacer = document.createElement('div');
              spacer.className = 'mb-ing__hold-space';
              spacer.setAttribute('aria-hidden', 'true');
              held.parentNode.insertBefore(spacer, held.nextSibling);
              held.classList.add('mb-ing__hold');
              var sizeHold = function () {
                held.style.setProperty('--mb-ing-hold-top', holdTop() + 'px');
                spacer.style.height = step() * (rows.length - 1) + 'px';
              };
              sizeHold();
              st = ScrollTrigger.create({
                trigger: spacer,
                /* The spacer's top is the held block's natural bottom, so it
                   reaching (hold top + block height) is the block reaching
                   its hold position. */
                start: function () {
                  return 'top ' + (holdTop() + held.offsetHeight) + 'px';
                },
                end: function () {
                  return '+=' + step() * (rows.length - 1);
                },
                invalidateOnRefresh: true,
                onRefreshInit: sizeHold,
                onUpdate: function (self) {
                  scrollActivate(rowAt(self.progress));
                }
              });
            } else {
              st = ScrollTrigger.create({
                trigger: section,
                start: desktop ? 'top 70%' : 'top 75%',
                end: desktop ? 'bottom 30%' : 'bottom 25%',
                invalidateOnRefresh: true,
                onUpdate: function (self) {
                  scrollActivate(rowAt(self.progress));
                }
              });
            }

            /* Entering from below (scrolling back up) starts on the last
               row; from above, on the first. */
            scrollActivate(rowAt(st.progress));

            return function () {
              section.removeAttribute('data-mb-ing-scroll');
              held.classList.remove('mb-ing__hold');
              held.style.removeProperty('--mb-ing-hold-top');
              if (spacer) spacer.remove();
            };
          }
        );

        /* Pictures, reviews and app widgets above the section keep changing
           the page height after load, and ScrollTrigger only re-measures on
           a window resize - so a trigger measured early starts before the
           block actually reaches its hold. Re-measure (debounced) whenever
           the page's height changes; one observer for the page. */
        if (document.readyState === 'complete') {
          ScrollTrigger.refresh();
        } else {
          window.addEventListener('load', function () {
            ScrollTrigger.refresh();
          }, { once: true });
        }
        if ('ResizeObserver' in window && !window.__mbIngHeightWatch) {
          var lastHeight = document.body.scrollHeight;
          var heightTimer = null;
          window.__mbIngHeightWatch = new ResizeObserver(function () {
            var height = document.body.scrollHeight;
            if (Math.abs(height - lastHeight) < 2) return;
            lastHeight = height;
            clearTimeout(heightTimer);
            heightTimer = setTimeout(function () {
              ScrollTrigger.refresh();
            }, 200);
          });
          window.__mbIngHeightWatch.observe(document.body);
        }
      }

      /*
        Fallback when GSAP did not load (blocked CDN): the same steps from an
        IntersectionObserver - invisible bands laid over the rows, and a
        thin line across the viewport at 45% names the band it is in.
      */
      function setUpObserver() {
        if (!('IntersectionObserver' in window)) return;

        var rail = document.createElement('div');
        rail.className = 'mb-ing__rail';
        rail.setAttribute('aria-hidden', 'true');
        var bands = rows.map(function (row) {
          var band = document.createElement('div');
          band.className = 'mb-ing__band';
          band.setAttribute('data-row', row);
          rail.appendChild(band);
          return band;
        });
        section.appendChild(rail);

        function layoutRail() {
          var box = section.getBoundingClientRect();
          var first = cells.filter(function (cell) {
            return cell.getAttribute('data-mb-ing-row') === rows[0];
          });
          var last = cells.filter(function (cell) {
            return cell.getAttribute('data-mb-ing-row') === rows[rows.length - 1];
          });
          if (!first.length || !last.length) return;
          var top = Math.min.apply(null, first.map(function (c) { return c.getBoundingClientRect().top; })) - box.top;
          var bottom = Math.max.apply(null, last.map(function (c) { return c.getBoundingClientRect().bottom; })) - box.top;
          rail.style.top = Math.round(top) + 'px';
          rail.style.height = Math.max(0, Math.round(bottom - top)) + 'px';
        }
        layoutRail();
        window.addEventListener('resize', layoutRail);

        var pending = null;
        var settle = null;
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) pending = entry.target.getAttribute('data-row');
            });
            clearTimeout(settle);
            settle = setTimeout(function () {
              if (pending) scrollActivate(pending);
            }, 90);
          },
          { rootMargin: '-45% 0px -54% 0px', threshold: 0 }
        );
        bands.forEach(function (band) {
          observer.observe(band);
        });
      }
    });
  }

  /* ------------------------------------------------------------ carousels */

  /**
   * Snap-scrolling carousel with arrows and dots. The track is a real
   * horizontal scroller, so touch/trackpad swiping works natively and the
   * controls only drive `scrollTo`.
   */
  function initCarousels(root) {
    $$('[data-mb-carousel]', root).forEach(function (section) {
      if (!once(section, 'carousel')) return;

      var track = $('[data-mb-carousel-track]', section);
      if (!track) return;

      var prev = $('[data-mb-carousel-prev]', section);
      var next = $('[data-mb-carousel-next]', section);
      var dotWrap = $('[data-mb-carousel-dots]', section);
      var controls = $('.mb-carousel__controls', section);
      var slides = [];
      /* Scroll offsets the track can actually come to rest on - one per
         slide, except that every slide past the end of the scroll range
         shares the final one. */
      var stops = [];
      var dots = [];
      var index = 0;
      var scrollable = true;

      /* Where the track rests with this slide snapped: its offset less the
         track's scroll padding (mobile Results centres its pair that way). */
      function offsetOf(slide) {
        var pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
        return Math.max(0, slide.offsetLeft - track.offsetLeft - pad);
      }

      function nearest(left) {
        var closest = 0;
        var best = Infinity;
        stops.forEach(function (stop, i) {
          var d = Math.abs(stop - left);
          if (d < best) { best = d; closest = i; }
        });
        return closest;
      }

      /*
        Slides are re-read rather than captured once: the ratings section
        filters and reorders its cards, and the dots and arrow bounds have to
        follow. Hidden slides are excluded, so a filtered-down carousel
        behaves as though the hidden cards were never there.
      */
      function readSlides() {
        slides = $$('[data-mb-carousel-slide]', track).filter(function (slide) {
          return !slide.hidden;
        });

        /*
          Pagination only earns its place when the row can actually move, so
          the controls follow measured overflow rather than the slide count:
          three before/after pairs that already fit the desktop width get no
          arrows or dots.

          One dot per resting position, not per slide. With three cards on
          screen the last two can never reach the left edge, and a dot per
          slide left two dots at the end that could never light up (and a
          "next" arrow that did nothing).
        */
        var max = track.scrollWidth - track.clientWidth;
        scrollable = max > 4;
        stops = [];
        slides.forEach(function (slide) {
          var left = Math.min(offsetOf(slide), max);
          if (!stops.length || left - stops[stops.length - 1] > 4) stops.push(left);
        });
        if (dotWrap) dotWrap.hidden = !scrollable;
        if (controls) controls.hidden = !scrollable;

        /*
          The dots are rebuilt only when their number changes. A phone fires
          `resize` every time its URL bar slides, and recreating the buttons
          each time restarted their fade and flashed the active dot.
        */
        var wanted = scrollable && stops.length > 1 ? stops.length : 0;
        if (dotWrap && wanted !== dots.length) {
          dots.forEach(function (dot) {
            dot.remove();
          });
          dots = [];
          for (var i = 0; i < wanted; i++) {
            dots.push(makeDot(i));
          }
        }

        if (index > stops.length - 1) index = Math.max(0, stops.length - 1);
      }

      function makeDot(i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mb-carousel__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', function () {
          go(i);
        });
        dotWrap.appendChild(dot);
        return dot;
      }

      /* The owner of the slides decides where the track rests (the ratings
         section may land a new page on its end), so the position is read
         back rather than assumed to be the start. */
      section.addEventListener('mb:carousel:refresh', function () {
        readSlides();
        index = nearest(track.scrollLeft);
        paint();
      });

      /*
        The slides are wider than their final size until their images arrive,
        so a single measurement at init reports overflow that is about to go
        away. Re-measure on load and on resize, and let the autoplay guard
        re-read `scrollable` with it.
      */
      function remeasure() {
        readSlides();
        index = nearest(track.scrollLeft);
        paint();
        section.dispatchEvent(new CustomEvent('mb:carousel:measure'));
      }
      window.addEventListener('load', remeasure);
      window.addEventListener('resize', remeasure);

      function paint() {
        dots.forEach(function (d, i) {
          d.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }

      function go(target) {
        if (!stops.length) return;
        index = Math.max(0, Math.min(stops.length - 1, target));
        track.scrollTo({ left: stops[index], behavior: 'smooth' });
        paint();
      }

      /*
        The arrows wrap - the first position's "previous" goes to the end
        and the last position's "next" back to the start - which is what the
        ritual steps and this carousel's own autoplay already do, and why the
        Figma draws both arrows at full strength on the first slide.
      */
      if (prev) prev.addEventListener('click', function () {
        go(index <= 0 ? stops.length - 1 : index - 1);
      });
      if (next) next.addEventListener('click', function () {
        go(index >= stops.length - 1 ? 0 : index + 1);
      });

      /*
        Opt-in mouse drag (`data-mb-carousel-drag` on the section). Touch
        already scrolls the track natively, so only a mouse pointer is
        handled: snap is suspended while the row follows the cursor, the
        nearest slide is snapped to on release, and the click that ends a
        real drag is swallowed so it never lands on a video control.
      */
      if (section.hasAttribute('data-mb-carousel-drag')) {
        var drag = null;
        var moved = false;

        track.addEventListener('pointerdown', function (event) {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          drag = { x: event.clientX, left: track.scrollLeft, id: event.pointerId };
          moved = false;
        });

        track.addEventListener('pointermove', function (event) {
          if (!drag || event.pointerId !== drag.id) return;
          var dx = event.clientX - drag.x;
          if (!moved && Math.abs(dx) < 5) return;
          if (!moved) {
            moved = true;
            track.setPointerCapture(event.pointerId);
            track.classList.add('is-dragging');
          }
          track.scrollLeft = drag.left - dx;
        });

        function endDrag(event) {
          if (!drag || (event && event.pointerId !== drag.id)) return;
          drag = null;
          if (!moved) return;
          track.classList.remove('is-dragging');
          /* The click that ends this drag (if any) is dispatched in the same
             turn; after that nothing may be swallowed. */
          setTimeout(function () { moved = false; }, 0);
          go(nearest(track.scrollLeft));
        }

        track.addEventListener('pointerup', endDrag);
        track.addEventListener('pointercancel', endDrag);
        track.addEventListener('lostpointercapture', endDrag);

        track.addEventListener('click', function (event) {
          if (moved) {
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          }
        }, true);

        /* Stop the browser's own image / video drag ghost. */
        track.addEventListener('dragstart', function (event) {
          event.preventDefault();
        });
      }

      var timer = null;
      track.addEventListener('scroll', function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          var closest = nearest(track.scrollLeft);
          if (closest !== index) {
            index = closest;
            paint();
          }
        }, 80);
      });

      /*
        Opt-in autoplay: `data-mb-carousel-autoplay="<ms>"` on the section.
        It wraps at the end by scrolling back to the first slide rather than
        cloning anything, so the markup stays exactly as Liquid rendered it.
        It pauses on hover and focus and while the tab is in the background,
        and it never runs when the track cannot scroll.
      */
      var delay = Number(section.getAttribute('data-mb-carousel-autoplay')) || 0;
      if (delay > 0) {
        var ticker = null;
        var held = false;

        var stopAuto = function () {
          clearInterval(ticker);
          ticker = null;
        };

        var startAuto = function () {
          if (ticker || held || !scrollable || stops.length < 2) return;
          ticker = setInterval(function () {
            if (document.hidden) return;
            go(index + 1 >= stops.length ? 0 : index + 1);
          }, delay);
        };

        var hold = function () {
          held = true;
          stopAuto();
        };
        var release = function () {
          held = false;
          startAuto();
        };

        section.addEventListener('mouseenter', hold);
        section.addEventListener('mouseleave', release);
        section.addEventListener('focusin', hold);
        section.addEventListener('focusout', release);
        /* A drag hands control to the shopper for as long as they hold it. */
        track.addEventListener('pointerdown', hold, { passive: true });
        track.addEventListener('pointerup', release, { passive: true });
        document.addEventListener('visibilitychange', function () {
          if (document.hidden) stopAuto();
          else startAuto();
        });

        section.addEventListener('mb:carousel:refresh', function () {
          stopAuto();
          startAuto();
        });
        section.addEventListener('mb:carousel:measure', function () {
          stopAuto();
          startAuto();
        });

        readSlides();
        paint();
        startAuto();
        return;
      }

      readSlides();
      paint();
    });
  }

  /* ------------------------------------------------------- ratings section */

  /**
   * Review filtering, the text/photo switch and sorting.
   *
   * All three act on the cards the section rendered rather than fetching
   * anything, so they behave the same whether the reviews come from theme
   * blocks today or from the reviews app later â swap where the cards come
   * from and this keeps working.
   *
   * The carousel itself is `initCarousels`; this only hides and reorders its
   * slides, then tells it to re-read them.
   */
  function initRatings(root) {
    $$('[data-mb-ratings]', root).forEach(function (section) {
      if (!once(section, 'ratings')) return;

      /*
        "Write A Review" drives Judge.me's own form rather than opening a
        second one: scroll its widget into view and click the trigger it
        renders. Judge.me hydrates asynchronously, so the trigger is looked
        up at click time and the widget is only scrolled to when it has not
        arrived yet.
      */
      var app = $('[data-mb-ratings-app]', section);

      /*
        Judge.me ships its widget with an inline
        `<style class="jdgm-temp-hiding-style">.jdgm-rev-widg{display:none}</style>`
        so the reviews cannot flash before their stylesheet lands. Their
        initialiser removes it once the widget is theirs to manage â but the
        widget here comes from the `judgeme.widget` metafield rather than
        being placed by the app, so nothing removes it and the reviews stay
        hidden.

        Performing that same reveal is what makes the metafield integration
        deterministic. It is their own step, not an override: no !important,
        and their real stylesheet still styles everything.
      */
      if (app) {
        var reveal = function () {
          $$('.jdgm-temp-hiding-style', app).forEach(function (style) {
            style.remove();
          });
        };
        reveal();
        /* Judge.me can re-render the widget after fetching; keep clearing
           the style it re-injects until the widget settles. */
        if ('MutationObserver' in window) {
          var seen = new MutationObserver(reveal);
          seen.observe(app, { childList: true, subtree: true });
          setTimeout(function () {
            seen.disconnect();
          }, 10000);
        }
      }

      var write = $('[data-mb-ratings-write]', section);
      if (write && app) {
        /*
          Judge.me's review form, opened through Judge.me.

          Two of their own steps are needed, in order: their write link
          renders the form markup into the widget's header, and
          `jdgm.openForm($widget)` is what actually reveals it, focuses the
          first field and wires the submit. Calling the second without the
          first finds nothing to open, which is what "Write a Review does
          nothing" was. Their initialiser is the fallback for a page where
          the link has not been bound yet.

          Everything here is their markup and their handlers - the form
          posts to Judge.me, with their fields, validation and photo upload
          intact.
        */
        var formOpenTries = 0;

        function openJudgemeForm() {
          var jd = window.jdgm;
          var wrapper = $('.jdgm-form-wrapper', app);

          if (!wrapper) {
            var trigger = $('.jdgm-write-rev-link', app);
            if (trigger) {
              trigger.click();
            } else if (jd && typeof jd.initializeWidgets === 'function' && !section.__mbJdgmSetUp) {
              section.__mbJdgmSetUp = true;
              try {
                jd.initializeWidgets();
              } catch (e) {
                /* Partial setup still renders the form. */
              }
            }
            wrapper = $('.jdgm-form-wrapper', app);
          }

          if (jd && typeof jd.openForm === 'function' && jd.$) {
            try {
              var $widget = jd.$('.jdgm-rev-widg', app);
              if ($widget && $widget.length) jd.openForm($widget);
            } catch (e) {
              /* Fall through to the retry below. */
            }
          }

          wrapper = $('.jdgm-form-wrapper', app);
          var shown = wrapper && wrapper.offsetParent !== null;
          formOpenTries += 1;
          /* Their markup arrives asynchronously on the first ask; give it a
             few beats rather than one shot. */
          if (!shown && formOpenTries < 16) {
            setTimeout(openJudgemeForm, 250);
          }
        }

        write.addEventListener('click', function () {
          /* Bring the widget back into the page before opening its form -
             it is held off-screen while it is only acting as the data
             source for the cards above. */
          app.classList.add('is-open');
          formOpenTries = 0;
          openJudgemeForm();
          app.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }

      /* --------------------------------------------------- Judge.me data */

      /**
       * Everything shown in this section comes from the widget's own markup:
       *
       *   .jdgm-rev-widg[data-average-rating][data-number-of-reviews]
       *   .jdgm-histogram__row[data-rating][data-percentage]
       *   .jdgm-rev[data-verified-buyer]
       *     .jdgm-rev__rating[data-score] / __title / __body
       *     .jdgm-rev__author / __location / __timestamp / __pics img
       *
       * Anything the widget does not publish is not shown. A product with no
       * reviews therefore gets the empty state rather than a placeholder
       * score, and the star breakdown is the product's real distribution
       * instead of five numbers typed into the theme editor.
       */
      var live = section.getAttribute('data-mb-ratings-source') === 'judgeme';

      function text(node) {
        return node ? (node.textContent || '').replace(/\s+/g, ' ').trim() : '';
      }

      function starRow(score) {
        var row = document.createElement('span');
        row.className = 'mb-stars';
        row.setAttribute('role', 'img');
        row.setAttribute('aria-label', score + ' out of 5');
        var template = $('[data-mb-ratings-stars] .mb-star', section);
        for (var i = 1; i <= 5; i += 1) {
          var slot = template
            ? template.cloneNode(true)
            : document.createElement('span');
          slot.className = 'mb-star' + (i <= score ? ' is-on' : '');
          row.appendChild(slot);
        }
        return row;
      }

      /**
       * Review photo viewer.
       *
       * Judge.me's own gallery belongs to a widget that is held off-screen,
       * so this is a small overlay of our own using their image URLs: one
       * per review, Escape or the backdrop or the button to close, arrows
       * when a review carries more than one photo, and the page scroll
       * locked while it is open (the same treatment the product gallery's
       * zoom uses).
       */
      var viewer = null;
      var viewerState = { urls: [], index: 0, lastFocus: null };

      function buildViewer() {
        if (viewer) return viewer;
        viewer = document.createElement('div');
        viewer.className = 'mb-revbox';
        viewer.hidden = true;
        viewer.setAttribute('role', 'dialog');
        viewer.setAttribute('aria-modal', 'true');
        viewer.setAttribute('aria-label', 'Review photo');
        viewer.innerHTML =
          '<img class="mb-revbox__img" alt="">' +
          '<button type="button" class="mb-revbox__close" aria-label="Close">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M5.5 5.5l13 13M18.5 5.5l-13 13"/></svg></button>' +
          '<button type="button" class="mb-revbox__step mb-revbox__step--prev" aria-label="Previous photo">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 5.5 8 12l6.5 6.5"/></svg></button>' +
          '<button type="button" class="mb-revbox__step mb-revbox__step--next" aria-label="Next photo">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 5.5 16 12l-6.5 6.5"/></svg></button>';
        document.body.appendChild(viewer);

        viewer.addEventListener('click', function (event) {
          if (event.target === viewer) closeViewer();
        });
        $('.mb-revbox__close', viewer).addEventListener('click', closeViewer);
        $('.mb-revbox__step--prev', viewer).addEventListener('click', function () {
          step(-1);
        });
        $('.mb-revbox__step--next', viewer).addEventListener('click', function () {
          step(1);
        });
        document.addEventListener('keydown', function (event) {
          if (viewer.hidden) return;
          if (event.key === 'Escape') closeViewer();
          if (event.key === 'ArrowLeft') step(-1);
          if (event.key === 'ArrowRight') step(1);
        });
        return viewer;
      }

      function paintViewer() {
        var many = viewerState.urls.length > 1;
        $('.mb-revbox__img', viewer).src = viewerState.urls[viewerState.index];
        $('.mb-revbox__step--prev', viewer).hidden = !many;
        $('.mb-revbox__step--next', viewer).hidden = !many;
      }

      function step(by) {
        var n = viewerState.urls.length;
        if (n < 2) return;
        viewerState.index = (viewerState.index + by + n) % n;
        paintViewer();
      }

      function openViewer(urls, index) {
        buildViewer();
        viewerState.urls = urls;
        viewerState.index = index || 0;
        viewerState.lastFocus = document.activeElement;
        paintViewer();
        viewer.hidden = false;
        /* Lock the page, not the overlay - the same lock the zoom uses. */
        document.documentElement.style.overflow = 'hidden';
        $('.mb-revbox__close', viewer).focus();
      }

      function closeViewer() {
        if (!viewer || viewer.hidden) return;
        viewer.hidden = true;
        document.documentElement.style.overflow = '';
        if (viewerState.lastFocus && viewerState.lastFocus.focus) {
          viewerState.lastFocus.focus();
        }
      }

      function buildCard(rev) {
        var ratingEl = $('.jdgm-rev__rating', rev);
        var score = Math.round(Number(ratingEl && ratingEl.getAttribute('data-score')) || 0);
        var title = text($('.jdgm-rev__title', rev));
        var body = text($('.jdgm-rev__body', rev));
        var author = text($('.jdgm-rev__author', rev));
        var place = text($('.jdgm-rev__location', rev));
        /* 2026-10-21: the place without brackets, plus the review date (Figma 2935:69124 meta line). */
        place = place.replace(/^\(\s*|\s*\)$/g, '');
        var dateEl = $('.jdgm-rev__timestamp', rev);
        var when = '';
        if (dateEl) {
          var d = new Date(dateEl.getAttribute('datetime') || '');
          if (!isNaN(d.getTime())) when = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }
        var verified = rev.getAttribute('data-verified-buyer') === 'true';
        /*
          Judge.me wraps every review photo in
          `<a class="jdgm-rev__pic-link" href="<full image url>">` around a
          lazily-loaded `<img data-src>` whose `src` stays empty until their
          own loader runs. Reading the link's `href` gives the real,
          complete URL for every photo on the review - all of them, not just
          the first - with no dependence on their lazy loader having fired.
        */
        var photoLinks = $$('.jdgm-rev__pic-link[href]', rev)
          .map(function (a) {
            return a.getAttribute('data-mfp-src') || a.getAttribute('href');
          })
          .filter(function (url) {
            return url && url.indexOf('http') === 0;
          });
        var photoCount = photoLinks.length;

        var card = document.createElement('article');
        card.className = 'mb-ratings__card';
        card.setAttribute('data-mb-carousel-slide', '');
        card.setAttribute('data-mb-review', '');
        card.setAttribute('data-rating', String(score));
        card.setAttribute('data-has-photo', photoCount ? 'true' : 'false');
        /* The chips filter on the review's own words - that is the only
           concern signal Judge.me gives us on this store. */
        card.setAttribute('data-text', (title + ' ' + body).toLowerCase());

        card.appendChild(starRow(score));

        if (photoLinks.length) {
          var strip = document.createElement('div');
          strip.className = 'mb-ratings__photos';
          photoLinks.forEach(function (url, i) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'mb-ratings__photo';
            btn.setAttribute('aria-label', 'Open review photo ' + (i + 1));
            btn.setAttribute('data-mb-review-photo', String(i));
            var img = document.createElement('img');
            img.src = url;
            img.alt = '';
            img.loading = 'lazy';
            /* No empty frames: a photo that will not load takes its own
               thumbnail with it, and the strip goes if it was the last. */
            img.addEventListener('error', function () {
              btn.remove();
              if (!strip.children.length) strip.remove();
            });
            btn.appendChild(img);
            btn.addEventListener('click', function () {
              openViewer(photoLinks, i);
            });
            strip.appendChild(btn);
          });
          card.appendChild(strip);
        }

        if (title) {
          var h = document.createElement('h3');
          h.className = 'mb-ratings__headline';
          h.textContent = title;
          card.appendChild(h);
        }

        if (body) {
          var p = document.createElement('p');
          p.className = 'mb-ratings__body';
          p.textContent = body;
          card.appendChild(p);
        }

        var foot = document.createElement('footer');
        foot.className = 'mb-ratings__author';

        var avatar = document.createElement('span');
        avatar.className = 'mb-ratings__avatar mb-ratings__avatar--initial';
        avatar.setAttribute('aria-hidden', 'true');
        avatar.textContent = (author.charAt(0) || '?').toUpperCase();
        foot.appendChild(avatar);

        var who = document.createElement('span');
        who.className = 'mb-ratings__author-text';
        var name = document.createElement('span');
        name.className = 'mb-ratings__name';
        name.textContent = author || 'Anonymous';
        who.appendChild(name);

        var metaBits = [];
        if (verified) metaBits.push(section.getAttribute('data-verified-label') || 'Verified buyer');
        if (place) metaBits.push(place);
        if (when) metaBits.push(when);
        if (metaBits.length) {
          var meta = document.createElement('span');
          meta.className = 'mb-ratings__meta';
          meta.textContent = metaBits.join(' - ');
          who.appendChild(meta);
        }
        foot.appendChild(who);
        card.appendChild(foot);
        return card;
      }

      /* A summary typed into the theme editor is the merchant's to show;
         only a blank one is filled from the widget. */
      var liveSummary = section.getAttribute('data-mb-ratings-summary') === 'live';

      function hydrate() {
        if (!live || !app) return;

        var widget = $('.jdgm-rev-widg', app);
        var reviews = $$('.jdgm-rev', app);
        var count = widget ? Number(widget.getAttribute('data-number-of-reviews')) || 0 : 0;
        var average = widget ? Number(widget.getAttribute('data-average-rating')) || 0 : 0;

        /* ------------------------------------------------------- summary */
        var scoreRow = liveSummary ? $('[data-mb-ratings-score]', section) : null;
        if (scoreRow) {
          var has = count > 0 && average > 0;
          scoreRow.hidden = !has;
          if (has) {
            var stars = $('[data-mb-ratings-stars]', section);
            if (stars) {
              var lit = Math.round(average);
              $$('.mb-star', stars).forEach(function (slot, i) {
                slot.classList.toggle('is-on', i < lit);
              });
              stars.setAttribute('aria-label', formatRating(average) + ' out of 5');
            }
            var value = $('[data-mb-ratings-value]', section);
            if (value) value.textContent = formatRating(average) + ' out of 5';
            var countEl = $('[data-mb-ratings-count]', section);
            var countSep = $('[data-mb-ratings-count-sep]', section);
            if (countEl) {
              countEl.textContent = count + (count === 1 ? ' review' : ' reviews');
              countEl.hidden = false;
            }
            if (countSep) countSep.hidden = false;
            var again = $('[data-mb-ratings-again]', section);
            var againSep = $('[data-mb-ratings-again-sep]', section);
            if (again) again.hidden = false;
            if (againSep) againSep.hidden = false;
          }
        }

        /* --------------------------------------------------------- bars */
        var bars = liveSummary ? $('[data-mb-ratings-bars]', section) : null;
        var rows = $$('.jdgm-histogram__row[data-rating]', app).filter(function (row) {
          return row.getAttribute('data-rating') !== 'null';
        });
        if (bars) {
          var any = false;
          rows.forEach(function (row) {
            var star = row.getAttribute('data-rating');
            var pct = Number(row.getAttribute('data-percentage'));
            var bar = $('[data-mb-ratings-bar="' + star + '"]', section);
            if (!bar || isNaN(pct)) return;
            any = true;
            $('.mb-ratings__bar-fill', bar).style.width = pct + '%';
            $('.mb-ratings__bar-pct', bar).textContent = pct + '%';
          });
          bars.hidden = !(any && count > 0);
        }

        /* -------------------------------------------------------- cards */
        if (!track) return;
        var signature = reviews
          .map(function (r) { return r.getAttribute('data-review-id') || ''; })
          .join('|');
        if (signature === builtFrom) return;
        builtFrom = signature;

        track.innerHTML = '';
        reviews.forEach(function (rev) {
          track.appendChild(buildCard(rev));
        });
        bindCards();
      }

      var track = $('[data-mb-carousel-track]', section);
      var chips = $$('[data-mb-ratings-filter]', section);
      var mediaToggle = $('[data-mb-ratings-media]', section);
      var sort = $('[data-mb-ratings-sort]', section);
      var empty = $('[data-mb-ratings-empty]', section);
      var tools = $('[data-mb-ratings-tools]', section);
      var filters = $('[data-mb-ratings-filters]', section);
      var carousel = $('.mb-ratings__carousel', section);

      var cards = [];
      var order = [];
      var builtFrom = null;
      var bound = false;
      var landAtEnd = false;

      var state = { concern: null, photosOnly: false, sort: 'featured' };

      function apply() {
        if (!cards.length) {
          if (carousel) carousel.hidden = true;
          if (tools) tools.hidden = true;
          if (filters) filters.hidden = true;
          if (empty) {
            /* "None yet" and "none matched that filter" are different
               things to say. */
            empty.textContent =
              section.getAttribute('data-empty-none') || empty.textContent;
            empty.hidden = false;
          }
          return;
        }
        if (carousel) carousel.hidden = false;
        /* A switch and a sort are only controls when there is more than one
           thing to switch or sort. */
        if (tools) tools.hidden = cards.length < 2;

        var sorted = order.slice();
        if (state.sort === 'highest' || state.sort === 'lowest') {
          var dir = state.sort === 'highest' ? -1 : 1;
          sorted.sort(function (a, b) {
            var d = (Number(a.getAttribute('data-rating')) - Number(b.getAttribute('data-rating'))) * dir;
            /* Stable within a rating: fall back to the authored order. */
            return d !== 0 ? d : order.indexOf(a) - order.indexOf(b);
          });
        }

        var shown = 0;
        sorted.forEach(function (card) {
          var byConcern = card.getAttribute('data-concern');
          var okConcern =
            !state.concern ||
            (byConcern
              ? byConcern === state.concern
              : (card.getAttribute('data-text') || '').indexOf(state.concernText) !== -1);
          var okMedia =
            !state.photosOnly || card.getAttribute('data-has-photo') === 'true';
          var visible = okConcern && okMedia;
          card.hidden = !visible;
          if (visible) shown += 1;
          if (track) track.appendChild(card);
        });

        if (empty) {
          empty.textContent =
            section.getAttribute('data-empty-filtered') || empty.textContent;
          empty.hidden = shown > 0;
        }
        /*
          A new slide set starts at its first card - except the page before
          this one, reached with the "previous" arrow, which is shown from its
          end so the arrow reads as a step back rather than a jump.
        */
        if (track) track.scrollLeft = landAtEnd ? track.scrollWidth : 0;
        landAtEnd = false;
        /*
          Let the carousel rebuild its dots around the new slide set. The
          listener is on the carousel element itself, which is a descendant
          of this section - a bubbling event from the section would travel
          away from it, not towards it.
        */
        (carousel || section).dispatchEvent(new CustomEvent('mb:carousel:refresh'));
      }

      /**
       * (Re)reads the cards in the track and wires the controls to them.
       * Called once for a server-rendered set and again whenever Judge.me
       * re-renders or paginates its widget.
       */
      function bindCards() {
        cards = $$('[data-mb-review]', section);
        order = cards.slice();

        /* Only offer a chip that can actually return a review. */
        chips.forEach(function (chip) {
          var value = chip.getAttribute('data-mb-ratings-filter');
          var label = (chip.textContent || '').toLowerCase().trim();
          var usable = cards.some(function (card) {
            var byConcern = card.getAttribute('data-concern');
            if (byConcern) return byConcern === value;
            var words = card.getAttribute('data-text') || '';
            return label && words.indexOf(label) !== -1;
          });
          var item = chip.parentElement;
          if (item) item.hidden = !usable;
        });
        if (filters) {
          filters.hidden = !chips.some(function (chip) {
            return chip.parentElement && !chip.parentElement.hidden;
          });
        }

        if (!bound) {
          bindControls();
          bound = true;
        }
        apply();
      }

      /* "All concerns" is pressed exactly when no concern is. */
      var allChip = $('[data-mb-ratings-all]', section);
      function syncAllChip() {
        if (allChip) allChip.setAttribute('aria-pressed', state.concern ? 'false' : 'true');
      }

      function bindControls() {
        chips.forEach(function (chip) {
          chip.addEventListener('click', function () {
            var value = chip.getAttribute('data-mb-ratings-filter');
            /* A second click on the live chip clears the filter. */
            var next = state.concern === value ? null : value;
            state.concern = next;
            state.concernText = next ? (chip.textContent || '').toLowerCase().trim() : '';
            chips.forEach(function (other) {
              other.setAttribute(
                'aria-pressed',
                other === chip && next !== null ? 'true' : 'false'
              );
            });
            syncAllChip();
            apply();
          });
        });

        if (allChip) {
          allChip.addEventListener('click', function () {
            state.concern = null;
            state.concernText = '';
            chips.forEach(function (other) {
              other.setAttribute('aria-pressed', 'false');
            });
            syncAllChip();
            apply();
          });
        }

        if (mediaToggle) {
          mediaToggle.addEventListener('click', function () {
            state.photosOnly = mediaToggle.getAttribute('aria-checked') !== 'true';
            mediaToggle.setAttribute('aria-checked', state.photosOnly ? 'true' : 'false');
            apply();
          });
        }

        if (sort) {
          var sortLabel = $('[data-mb-ratings-sort-label]', section);
          /*
            With Judge.me live, only one page of reviews is on the page, so
            reordering those cards cannot surface the lowest-rated review of
            a hundred. The choice is handed to the widget's own sort instead:
            Judge.me reloads the page of reviews server-side, and the
            MutationObserver below rebuilds the cards from it. "Featured" is
            whatever order the widget started in. The local sort still runs
            on the current cards, so the theme-block reviews sort too.
          */
          var jdgmSortFor = { highest: 'highest-rating', lowest: 'lowest-rating' };
          var jdgmSortDefault = null;
          var driveJudgeme = function (value) {
            /* By its options, not its class: Judge.me's review form puts the
               same class on its "name format" select (value `last_initial`),
               which loads later and would otherwise be found first. */
            var jd = app
              ? $$('select.jdgm-sort-dropdown', app).filter(function (el) {
                  return !!el.querySelector('option[value="lowest-rating"]');
                })[0]
              : null;
            if (!jd) return;
            if (jdgmSortDefault === null) jdgmSortDefault = jd.value;
            var target = jdgmSortFor[value] || jdgmSortDefault;
            if (!target || jd.value === target) return;
            jd.value = target;
            /* Judge.me binds its handler on the select through its own
               bundled jQuery (`jdgm.$`), so it is triggered through that -
               the theme's `window.jQuery` is a different copy and never
               reaches it. A native event is the fallback. */
            if (window.jdgm && window.jdgm.$) {
              window.jdgm.$(jd).trigger('change');
            } else {
              jd.dispatchEvent(new Event('change', { bubbles: true }));
            }
          };
          sort.addEventListener('change', function () {
            state.sort = sort.value;
            /* The visible text is the label; the select underneath it is
               invisible, so it has to be told what was chosen. */
            if (sortLabel) {
              sortLabel.textContent = sort.options[sort.selectedIndex].textContent;
            }
            driveJudgeme(sort.value);
            apply();
          });
        }

        /* Honour the chip the section rendered as pressed. */
        var initial = chips.filter(function (chip) {
          return chip.getAttribute('aria-pressed') === 'true';
        })[0];
        if (initial) {
          state.concern = initial.getAttribute('data-mb-ratings-filter');
          state.concernText = (initial.textContent || '').toLowerCase().trim();
        }
        syncAllChip();
      }

      /*
        Judge.me pages its reviews five at a time (21 pages on this product),
        so the arrows carry on into the next page once the loaded set has
        been scrolled through. Their own pagination link does the fetching;
        the MutationObserver below rebuilds the cards from whatever it
        returns, so this adds no second source of review data.
      */
      function paginate(dir) {
        if (!live || !app || !track) return false;
        var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        var atStart = track.scrollLeft <= 4;
        var link =
          dir > 0
            ? atEnd && $('.jdgm-paginate__next-page', app)
            : atStart && $('.jdgm-paginate__prev-page', app);
        if (!link) return false;
        builtFrom = null;
        landAtEnd = dir < 0;
        link.click();
        return true;
      }

      var nextBtn = $('[data-mb-carousel-next]', section);
      var prevBtn = $('[data-mb-carousel-prev]', section);
      if (nextBtn) nextBtn.addEventListener('click', function () { paginate(1); });
      if (prevBtn) prevBtn.addEventListener('click', function () { paginate(-1); });

      if (live) {
        hydrate();
        /*
          Judge.me hydrates and paginates asynchronously, so the cards are
          rebuilt whenever its markup settles. `hydrate` compares review ids
          before touching the DOM, so a re-render that changes nothing costs
          one string compare.
        */
        if (app && 'MutationObserver' in window) {
          var watcher = new MutationObserver(hydrate);
          watcher.observe(app, { childList: true, subtree: true });
        }
      } else {
        bindCards();
      }
    });
  }

  /* ----------------------------------------------------------- step slider */

  function initSteps(root) {
    $$('[data-mb-steps]', root).forEach(function (section) {
      if (!once(section, 'steps')) return;

      var steps = $$('[data-mb-step]', section);
      var media = $$('[data-mb-step-media]', section);
      if (steps.length < 2) return;

      var prev = $('[data-mb-steps-prev]', section);
      var next = $('[data-mb-steps-next]', section);
      var dotWrap = $('[data-mb-steps-dots]', section);
      var dots = [];
      var index = 0;

      if (dotWrap) {
        steps.forEach(function (_, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'mb-carousel__dot';
          dot.setAttribute('aria-label', 'Show step ' + (i + 1));
          dot.addEventListener('click', function () { show(i); });
          dotWrap.appendChild(dot);
          dots.push(dot);
        });
      }

      function show(target) {
        index = (target + steps.length) % steps.length;
        steps.forEach(function (el, i) { el.hidden = i !== index; });
        media.forEach(function (el, i) { el.hidden = i !== index; });
        dots.forEach(function (d, i) {
          d.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }

      if (prev) prev.addEventListener('click', function () { show(index - 1); });
      if (next) next.addEventListener('click', function () { show(index + 1); });

      show(0);
    });
  }

  /* ------------------------------------------------------------- ugc video */

  function initUgc(root) {
    $$('[data-mb-ugc-play]', root).forEach(function (btn) {
      if (!once(btn, 'ugcplay')) return;
      btn.addEventListener('click', function () {
        var video = $('video', btn.closest('.mb-ugc__media'));
        if (!video) return;
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });
    });

    $$('[data-mb-ugc-mute]', root).forEach(function (btn) {
      if (!once(btn, 'ugcmute')) return;
      btn.addEventListener('click', function () {
        var video = $('video', btn.closest('.mb-ugc__media'));
        if (!video) return;
        video.muted = !video.muted;
        btn.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
        btn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      });
    });
  }

  /* ------------------------------------------------------------ video wall */

  /**
   * Every Moonbow video, wherever it is on the page.
   *
   * The design allows exactly two controls â play/pause and mute/unmute â
   * so the markup ships `controls: false` and these buttons drive the
   * element directly. Three rules the design asks for are enforced here
   * rather than per-section:
   *
   *  - audio is exclusive: unmuting one video mutes every other one;
   *  - a video the shopper paused stays paused, even when the carousel
   *    scrolls it out of view and back, or a slider autoplays past it;
   *  - videos only play while they are actually on screen, so a page of
   *    clips does not decode a dozen streams at once.
   *
   * `wanted` is the shopper's intent (did they pause this?), kept separate
   * from whether the element happens to be playing right now.
   */
  var videoWall = (function () {
    var all = [];

    function mark(video) {
      var box = video.closest('[data-mb-video]') || video.parentElement;
      if (!box) return;
      /* Every play hook in the box, not just the first: the clip row carries a
         corner control and a cue in the middle of the picture, and both have
         to re-label together. */
      var pauses = $$('[data-mb-video-play]', box);
      var mute = $('[data-mb-video-mute]', box);
      if (pauses.length) {
        pauses.forEach(function (btn) {
          btn.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
        });
        box.classList.toggle('is-paused', video.paused);
      }
      if (mute) {
        mute.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
        mute.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        box.classList.toggle('is-muted', video.muted);
      }
    }

    /** Autoplay can be refused; swallow it rather than throwing. */
    function play(video) {
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function () {
          /* Audible playback refused (no fresh gesture, mostly iOS Safari):
             fall back to muted so the button never claims sound it lacks. */
          if (!video.muted) {
            video.muted = true;
            mark(video);
            var retry = video.play();
            if (retry && typeof retry.catch === 'function') retry.catch(function () {});
          }
        });
      }
    }

    function reduced() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    return {
      add: function (video) {
        if (all.indexOf(video) > -1) return;
        video.__mbWanted = true;
        all.push(video);
        mark(video);
      },

      /** Exclusive audio: at most one video on the page is ever unmuted. */
      unmute: function (video) {
        all.forEach(function (other) {
          if (other === video) return;
          other.muted = true;
          mark(other);
        });
        video.muted = false;
        mark(video);
      },

      mute: function (video) {
        video.muted = true;
        mark(video);
      },

      /**
       * Play with sound, as the direct result of a shopper's click - the
       * browser only allows audible playback inside that gesture, so this
       * must run synchronously from the click handler. Marks the video as
       * wanted so the observer keeps it running while it is on screen.
       */
      listen: function (video) {
        this.unmute(video);
        video.__mbWanted = true;
        video.__mbChosen = true;
        if (video.paused) play(video);
        mark(video);
      },

      toggle: function (video) {
        video.__mbWanted = video.paused;
        video.__mbChosen = true;
        if (video.paused) {
          play(video);
        } else {
          video.pause();
        }
        mark(video);
      },

      /** Called by the observer and by slider events. */
      sync: function (video, onScreen) {
        if (!video.__mbWanted) return;
        /* Reduced motion: nothing starts by itself; a clip the shopper played
           (play or sound button) still resumes when it scrolls back. */
        if (onScreen && video.paused && reduced() && !video.__mbChosen) return;
        if (onScreen && video.paused) {
          play(video);
        } else if (!onScreen && !video.paused) {
          video.pause();
        }
      },

      mark: mark
    };
  })();

  function initVideoWall(root) {
    $$('[data-mb-video-wall]', root).forEach(function (wall) {
      if (!once(wall, 'videowall')) return;

      var boxes = $$('[data-mb-video]', wall);
      if (!boxes.length) return;

      boxes.forEach(function (box) {
        var video = $('video', box);
        if (!video) return;

        /* Belt and braces: the tag already carries these, but a Shopify
           video_tag can be re-rendered by the Theme Editor without them. */
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.removeAttribute('controls');

        videoWall.add(video);

        /* Both play hooks - the corner control and the clip row's centre cue -
           drive the same toggle. */
        $$('[data-mb-video-play]', box).forEach(function (btn) {
          btn.addEventListener('click', function () {
            videoWall.toggle(video);
          });
        });

        var mute = $('[data-mb-video-mute]', box);
        if (mute) {
          mute.addEventListener('click', function () {
            if (video.muted) {
              videoWall.unmute(video);
              /* Unmuting implies wanting it to play. */
              if (video.paused) videoWall.toggle(video);
            } else {
              videoWall.mute(video);
            }
          });
        }

        ['play', 'pause', 'volumechange'].forEach(function (name) {
          video.addEventListener(name, function () {
            videoWall.mark(video);
          });
        });
      });

      /* Only decode what is on screen. Without IntersectionObserver every
         video simply plays, which is the old behaviour and still correct. */
      if (!('IntersectionObserver' in window)) {
        boxes.forEach(function (box) {
          var video = $('video', box);
          if (video) videoWall.sync(video, true);
        });
        return;
      }

      var watcher = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = $('video', entry.target);
            if (video) videoWall.sync(video, entry.isIntersecting);
          });
        },
        { threshold: 0.4 }
      );

      boxes.forEach(function (box) {
        watcher.observe(box);
      });
    });
  }

  /* ---------------------------------------------------------- testimonials */

  /**
   * The full-width customer-video carousel.
   *
   * Swiper is vendored as a theme asset and the section loads it with
   * `defer`, so it may not have parsed by the time this runs â hence the
   * poll. Everything about the videos themselves belongs to `initVideoWall`;
   * this only owns the slider.
   */
  function initTestimonials(root) {
    $$('[data-mb-testimonials]', root).forEach(function (section) {
      if (!once(section, 'testimonials')) return;

      var el = $('.mb-testi__swiper', section);
      if (!el) return;

      var delay = Number(section.getAttribute('data-autoplay-delay')) || 0;

      function build() {
        /* Two callers now - the script tag's `load` and the poll - so this
           has to be idempotent. */
        if (el.swiper) return true;
        if (!window.Swiper) return false;

        /*
          Slide geometry from the Figma: 362-wide cards 12px apart with the
          active one at 419, which is 3.78 of them across the 1417 frame.
          `loop` is Swiper's own - it manages the wrap itself rather than the
          markup carrying duplicate slides.
        */
        /*
          Swiper only wraps when it has `slidesPerView + ceil(slidesPerView)`
          slides to work with; below that it disables `loop` and the reel
          dead-ends on the last card. `rewind` covers that case - it returns
          to the first clip at the end - so the reel runs continuously
          whatever the merchant has filled in, and neither path clones a
          slide into the markup.
        */
        var count = $$('.swiper-slide', el).length;
        /* The same three figures the breakpoints below use, so the loop test
           measures the view the reel actually renders. */
        var perView = window.matchMedia('(min-width: 1024px)').matches
          ? 3.882
          : window.matchMedia('(min-width: 768px)').matches
            ? 2.2
            : 1.2315;
        /* Swiper's own threshold, plus the two spare slides it keeps either
           side - with a fractional `slidesPerView` it stalls on `slideNext`
           when it has none. */
        var canLoop = count >= perView + Math.ceil(perView) + 2;

        /*
          A centred reel sitting on its first slide leaves the whole left half
          of the frame empty, because there is nothing before slide 0 to fill
          it. `loop` normally hides that by wrapping the reel, but it needs
          roughly ten clips at the desktop view and the merchant may have
          filled in fewer. Opening on a slide far enough in that both edges
          still carry a card reproduces what the Figma draws - a partial clip
          bleeding off each side - without cloning anything.
        */
        var initialSlide = 0;
        if (!canLoop && count > perView) {
          initialSlide = Math.min(
            count - 1,
            Math.max(0, Math.ceil((count - perView) / 2))
          );
        }

        var options = {
          /*
            The 375 frame draws a 303 card with its neighbours peeking 28 in
            from each edge and 8 between, so slidesPerView is (375 + 8) /
            (303 + 8). Swiper sizes a fractional view as
            (container - gap * (spv - 1)) / spv, which lands on 303 exactly.
          */
          slidesPerView: 1.2315,
          centeredSlides: true,
          spaceBetween: 8,
          /*
            Shared with the stylesheet's `--mb-testi-speed`. The centre
            card's resize and the neighbours' 8.01% step are CSS
            transitions; they have to run for exactly as long as Swiper
            translates the wrapper, or the reel arrives in two stages.
          */
          speed: 600,
          loop: canLoop,
          rewind: !canLoop && count > 1,
          initialSlide: initialSlide,
          loopAdditionalSlides: 2,
          watchSlidesProgress: true,
          /*
            Dragging. Swiper follows a mouse drag and a touch swipe out of
            the box, but its default long-swipe rule wants half a card before
            it moves on - an ordinary, unhurried drag across a 362px card
            snapped back and read as "the slider doesn't drag". A quarter of
            a card is enough here, and the grab cursor says it can be dragged.
            A drag never reaches the video underneath (`preventClicks`).
          */
          grabCursor: true,
          longSwipesRatio: 0.25,
          threshold: 6,
          navigation: {
            prevEl: $('.mb-testi__arrow--prev', section),
            nextEl: $('.mb-testi__arrow--next', section)
          },
          breakpoints: {
            768: { slidesPerView: 2.2, spaceBetween: 12 },
            /*
              The 1440 frame measures 362 / 363 across the side cards with 12
              between and 420 across the enlarged centre one: (1440 + 12) /
              (362 + 12) = 3.882. At 3.78 every card came out 10 wider, which
              carried into the product card on top of it.
            */
            1024: { slidesPerView: 3.882, spaceBetween: 12 }
          }
        };

        /* No ambient slide movement for visitors who ask for reduced motion. */
        if (delay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          options.autoplay = {
            delay: delay * 1000,
            /* Keep advancing after a swipe â the design treats this as an
               ambient reel, not a control the shopper has to drive. */
            disableOnInteraction: false,
            /* Hovering pauses the reel, not the videos: the shopper is
               reading a card and should not have it slide away. */
            pauseOnMouseEnter: true
          };
        }

        var swiper = new window.Swiper(el, options);

        /*
          Arrow placement, measured off the reel rather than guessed. The
          Figma puts the arrows 44px inside the outer edge of each side card
          on desktop and 18px inside the single centred card below that, and
          both insets scale with the card. Published as a length so the
          stylesheet needs no breakpoint of its own.
        */
        function placeArrows() {
          var slide = swiper.slidesSizesGrid && swiper.slidesSizesGrid[0];
          if (!slide || !swiper.size) return;
          var wide = window.matchMedia('(min-width: 1024px)').matches;
          var offset = wide
            ? (slide * 1.1602) / 2 + options.spaceBetween + slide - 44 * (slide / 362)
            : slide / 2 - 18 * (slide / 325);
          section.style.setProperty(
            '--mb-testi-arrow',
            Math.round(swiper.size / 2 - offset) + 'px'
          );
        }

        placeArrows();
        swiper.on('resize', placeArrows);
        swiper.on('breakpoint', placeArrows);
        window.addEventListener('load', placeArrows);

        /*
          Clicking a clip only brings it to the centre (Swiper's own slideTo /
          slideToLoop, so the scale, gap and transition match the arrows').
          Sound comes from the clip's sound button alone - a tap on the
          picture while swiping must never start audio. A click on a side
          clip's sound button centres it too: the button's own handler
          unmutes it first, and the slideChange rule below keeps it audible.
          A drag never counts as a click (see `allowClick`).
        */
        el.addEventListener('click', function (event) {
          /* `allowClick` is Swiper's own "that was a drag" flag; it is still
             false for the click that ends a swipe. */
          if (!swiper.allowClick) return;
          var slide = event.target.closest('.swiper-slide');
          if (!slide || !el.contains(slide)) return;

          if (!slide.classList.contains('swiper-slide-active')) {
            if (swiper.params.loop) {
              swiper.slideToLoop(Number(slide.getAttribute('data-swiper-slide-index')) || 0);
            } else {
              swiper.slideTo(swiper.slides.indexOf(slide));
            }
          }
        });

        /*
          Sound follows the centre: while any clip in the reel has sound,
          every slide change (arrow, swipe, drag, click) hands it to the new
          centre clip and mutes the one left behind. slideChange fires inside
          the shopper's gesture, so audible playback is allowed; if a browser
          still refuses, videoWall falls back to muted.
        */
        swiper.on('slideChange', function () {
          var audible = $$('video', el).some(function (v) {
            return !v.muted;
          });
          if (!audible) return;
          var active = swiper.slides[swiper.activeIndex];
          var video = active && $('video', active);
          if (video) {
            if (video.muted) videoWall.listen(video);
          } else {
            /* A still-image card in the centre: no clip keeps sound off-centre. */
            $$('video', el).forEach(function (v) {
              if (!v.muted) videoWall.mute(v);
            });
          }
        });

        /*
          An ambient reel would carry the clip the shopper is listening to
          off-centre a few seconds later, so autoplay waits while any clip in
          the reel has sound and picks up again once they are all muted.
        */
        if (swiper.autoplay && options.autoplay) {
          $$('video', el).forEach(function (video) {
            video.addEventListener('volumechange', function () {
              var audible = $$('video', el).some(function (v) {
                return !v.muted;
              });
              if (audible && swiper.autoplay.running) swiper.autoplay.stop();
              else if (!audible && !swiper.autoplay.running) swiper.autoplay.start();
            });
          });
        }

        /*
          `loopPreventsSliding` is on by default, so Swiper ignores next/prev
          while `animating` is true - which is what keeps rapid clicking from
          queueing up and drifting. It clears that flag on the wrapper's
          `transitionend`, and a missed event (a backgrounded tab, a section
          hidden mid-transition) would otherwise deadlock the reel for good.
          This is the watchdog for that: one timer per transition, a hair
          longer than the transition itself.
        */
        var guard = null;
        swiper.on('transitionStart', function () {
          clearTimeout(guard);
          guard = setTimeout(function () {
            if (swiper.animating) swiper.animating = false;
          }, options.speed + 150);
        });
        swiper.on('transitionEnd', function () {
          clearTimeout(guard);
        });

        return true;
      }

      if (build()) return;

      /*
        Swiper is deferred. The script tag's own `load` is the reliable
        signal - a fixed poll gave up after four seconds and left the reel
        as a static row whenever the asset was slow to arrive. The poll
        stays as a backstop for the case where the tag has already fired
        by the time this runs, and it now runs long enough to outlast a
        cold cache. The slides stay readable as a plain row in the
        meantime because `.swiper-wrapper` is a flex container.
      */
      var tag = document.querySelector('script[src*="swiper.bundle"]');
      if (tag) {
        tag.addEventListener('load', build);
      }

      var tries = 0;
      var timer = setInterval(function () {
        tries += 1;
        if (build() || tries > 150) clearInterval(timer);
      }, 100);
    });
  }

  /* ------------------------------------------------------------ newsletter */

  function initFooter(root) {
    $$('[data-mb-footer]', root).forEach(function (footer) {
      if (!once(footer, 'footer')) return;

      $$('[data-mb-localization-select]', footer).forEach(function (select) {
        select.addEventListener('change', function () {
          var form = select.closest('form');
          if (form) form.submit();
        });
      });
    });
  }

  /* ------------------------------------------------------------- lifecycle */

  /**
   * The claims marquee.
   *
   * The section renders four copies of the claim list and the keyframe moves
   * the track by exactly one of them. That is seamless only while the three
   * copies that are *not* scrolling off still cover the viewport â with a
   * short claim list, or a very wide screen, the band runs out of content
   * and the tail of the cycle shows an empty strip.
   *
   * This measures one copy and repeats the items inside every copy equally
   * until they do cover it. Repeating inside the lists (rather than adding
   * lists) keeps the -25% translate exact, so no keyframe maths changes, and
   * the animation is slowed in proportion so the claims keep travelling at
   * the speed the merchant set.
   */
  function initMarquee(root) {
    $$('[data-mb-marquee]', root).forEach(function (viewport) {
      if (!once(viewport, 'marquee')) return;

      var lists = $$('[data-mb-marquee-list]', viewport);
      var track = $('.mb-marquee__track', viewport);
      if (!lists.length || !track) return;

      /* The markup as rendered is the unit we repeat. */
      var originals = lists.map(function (list) {
        return Array.prototype.slice.call(list.children).map(function (item) {
          return item;
        });
      });
      var baseDuration = parseFloat(getComputedStyle(track).animationDuration) || 45;
      var applied = 1;

      function fit() {
        var unit = originals[0].length;
        if (!unit) return;

        /* Width of one un-repeated copy: the current list width over however
           many times it is currently repeated. */
        var listWidth = lists[0].getBoundingClientRect().width / applied;
        if (!listWidth) return;

        /*
          Three copies travel past while the fourth scrolls off, so the band
          is covered while 3 x (repeats x listWidth) >= viewport. The +1 on
          the viewport is sub-pixel slack, not a spare repeat - rounding it
          up unconditionally doubled the DOM on every page that was already
          wide enough, and halved the scroll speed with it.
        */
        var needed = Math.ceil((viewport.getBoundingClientRect().width + 1) / (listWidth * 3));
        if (needed < 1) needed = 1;
        if (needed === applied) return;

        lists.forEach(function (list, i) {
          var items = originals[i];
          if (needed > applied) {
            var frag = document.createDocumentFragment();
            for (var r = applied; r < needed; r++) {
              items.forEach(function (item) {
                var clone = item.cloneNode(true);
                clone.removeAttribute('id');
                clone.setAttribute('aria-hidden', 'true');
                frag.appendChild(clone);
              });
            }
            list.appendChild(frag);
          } else {
            while (list.children.length > items.length * needed) {
              list.removeChild(list.lastElementChild);
            }
          }
        });

        applied = needed;
        /* Longer track, same apparent speed. */
        track.style.animationDuration = baseDuration * applied + 's';
      }

      fit();
      window.addEventListener('load', fit);

      var timer = null;
      window.addEventListener('resize', function () {
        clearTimeout(timer);
        timer = setTimeout(fit, 150);
      });
    });
  }

  /* ----------------------------------------------- spotlight product crop */

  /**
   * The spotlight's product framing.
   *
   * The artboard's artwork is a tight cut-out and the designer zooms into it
   * â the product's ink fills about 96% of the 383 x 372 box. The store's
   * media is a studio shot with its own margin, and that margin is not
   * consistent: across this catalogue the product occupies anywhere from 60%
   * to 93% of the canvas. So a single crop, zoom or `object-fit` cannot
   * reproduce the drawn framing, and a fixed factor would either leave the
   * small shots floating or slice the edges off the wide ones.
   *
   * This measures each asset instead. Shopify's CDN serves product images
   * with `access-control-allow-origin: *`, so a 96px copy can be read on a
   * canvas: the backdrop colour comes from the corners, the ink box from
   * every pixel that differs from it (or, for a real cut-out, from alpha),
   * and the image is then scaled and offset so that ink lands centred and
   * filling the frame â which is exactly what the Figma node does with its
   * own transform.
   *
   * Everything about it degrades quietly. A tainted canvas, a missing image
   * or an unreadable one leaves the picture exactly as the CSS drew it.
   */
  var SPOT_FILL = 0.96; /* the artboard's ink-to-frame ratio */
  var SPOT_MAX_SCALE = 1.8; /* keeps the crop inside the 2x source */

  function measureInk(url) {
    return new Promise(function (resolve) {
      var probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onerror = function () {
        resolve(null);
      };
      probe.onload = function () {
        try {
          var w = 96;
          var h = Math.max(1, Math.round((probe.naturalHeight / probe.naturalWidth) * w));
          var canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          var ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(probe, 0, 0, w, h);
          var data = ctx.getImageData(0, 0, w, h).data;

          /* The backdrop is whatever the four corners agree on. */
          var corners = [0, w - 1, (h - 1) * w, h * w - 1].map(function (i) {
            return [data[i * 4], data[i * 4 + 1], data[i * 4 + 2], data[i * 4 + 3]];
          });
          var bg = [0, 1, 2].map(function (c) {
            return corners.reduce(function (sum, px) {
              return sum + px[c];
            }, 0) / 4;
          });
          var bgAlpha = corners.reduce(function (sum, px) {
            return sum + px[3];
          }, 0) / 4;

          var x0 = w, y0 = h, x1 = -1, y1 = -1;
          for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
              var i = (y * w + x) * 4;
              var isInk;
              if (bgAlpha < 32) {
                /* A real cut-out: ink is simply what is opaque. */
                isInk = data[i + 3] > 40;
              } else {
                isInk =
                  Math.abs(data[i] - bg[0]) +
                    Math.abs(data[i + 1] - bg[1]) +
                    Math.abs(data[i + 2] - bg[2]) >
                  30;
              }
              if (!isInk) continue;
              if (x < x0) x0 = x;
              if (x > x1) x1 = x;
              if (y < y0) y0 = y;
              if (y > y1) y1 = y;
            }
          }

          if (x1 < x0 || y1 < y0) {
            resolve(null);
            return;
          }

          resolve({
            /* Normalised, so they hold at any rendered size. */
            cx: (x0 + x1 + 1) / 2 / w,
            cy: (y0 + y1 + 1) / 2 / h,
            fw: (x1 - x0 + 1) / w,
            fh: (y1 - y0 + 1) / h,
            ratio: probe.naturalWidth / probe.naturalHeight
          });
        } catch (e) {
          /* Tainted canvas, or no 2d context. Leave the picture alone. */
          resolve(null);
        }
      };
      probe.src = url;
    });
  }

  /** A 96px-wide copy of whatever Shopify URL the <img> resolved to. */
  function thumbUrl(src) {
    if (!src) return null;
    if (/[?&]width=/.test(src)) return src.replace(/([?&]width=)\d+/, '$1' + 96);
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=96';
  }

  function initSpotlightCrop(root) {
    $$('.mb-spot__media img', root).forEach(function (img) {
      if (!once(img, 'spotcrop')) return;

      var box = img.parentElement;
      var ink = null;

      function apply() {
        if (!ink || !box) return;
        var bw = box.clientWidth;
        var bh = box.clientHeight;
        if (!bw || !bh) return;

        /* Where `object-fit: contain` puts the picture inside the box. */
        var rw, rh;
        if (bw / bh > ink.ratio) {
          rh = bh;
          rw = bh * ink.ratio;
        } else {
          rw = bw;
          rh = bw / ink.ratio;
        }

        var scale = Math.min(
          (SPOT_FILL * bw) / (rw * ink.fw),
          (SPOT_FILL * bh) / (rh * ink.fh)
        );
        if (!isFinite(scale) || scale <= 1.02) {
          img.style.transform = '';
          return;
        }
        scale = Math.min(scale, SPOT_MAX_SCALE);

        /* Scale happens about the centre, so re-centre on the ink after it. */
        var dx = -scale * (ink.cx - 0.5) * rw;
        var dy = -scale * (ink.cy - 0.5) * rh;
        img.style.transform =
          'translate(' + dx.toFixed(2) + 'px, ' + dy.toFixed(2) + 'px) scale(' + scale.toFixed(4) + ')';
      }

      function start() {
        var url = thumbUrl(img.currentSrc || img.src);
        if (!url) return;
        measureInk(url).then(function (result) {
          if (!result) return;
          ink = result;
          apply();
        });
      }

      if (img.complete && (img.currentSrc || img.src)) {
        start();
      } else {
        img.addEventListener('load', start, { once: true });
      }

      var timer = null;
      window.addEventListener('resize', function () {
        clearTimeout(timer);
        timer = setTimeout(apply, 150);
      });
    });
  }

  /* ------------------------------------------------------- collection page */

  /**
   * A review score to one decimal, truncated - the JS twin of the
   * `moonbow-rating-value` snippet. Judge.me's 4.95 is "4.9", never "5.0"
   * (`toFixed(1)` rounds it up); a true 5 is "5.0". The small nudge absorbs
   * float noise (4.3 * 10 = 42.99...).
   */
  function formatRating(value) {
    return (Math.floor(Number(value) * 10 + 1e-4) / 10).toFixed(1);
  }

  /**
   * `money_without_trailing_zeros` is what the collection card and the main
   * product section render from Liquid, so anything JavaScript writes back
   * into the same slots has to drop a whole-number's cents too or the price
   * visibly changes shape the moment a shopper switches purchase option.
   */
  function formatCardMoney(cents) {
    return formatMoney(cents).replace(/([.,])00(?!\d)/, '');
  }

  /**
   * The filter / sort pills.
   *
   * One open panel at a time, closed by a click outside or Escape. The sort
   * radios submit as soon as one is picked â a single choice has nothing to
   * accumulate â while the filter checkboxes wait for the panel's own Apply
   * button, so two facets can be chosen in one trip. Both paths are plain
   * form submits against `collection.url`, which is what makes the whole bar
   * work with the script switched off.
   */
  function initCollectionFacets(section) {
    var form = $('[data-mb-facets]', section);
    if (!form) return;

    var drops = $$('[data-mb-cdrop]', form);
    var hoverMq = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1024px)');
    function canHover() {
      return hoverMq.matches;
    }

    function closeAll(except) {
      drops.forEach(function (drop) {
        if (drop === except) return;
        var trigger = $('[data-mb-cdrop-trigger]', drop);
        var panel = $('[data-mb-cdrop-panel]', drop);
        if (!trigger || !panel) return;
        panel.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      });
    }

    drops.forEach(function (drop) {
      var trigger = $('[data-mb-cdrop-trigger]', drop);
      var panel = $('[data-mb-cdrop-panel]', drop);
      if (!trigger || !panel) return;

      panel.hidden = true;
      trigger.addEventListener('click', function () {
        /* Already opened by hover: the click that follows keeps it open
           rather than toggling it straight shut. */
        if (canHover() && !panel.hidden) return;
        var open = panel.hidden;
        closeAll(drop);
        panel.hidden = !open;
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      /* 2026-10-21 hover dropdowns (Iram): with a mouse on desktop, the
         panel opens on hover and closes when the pointer leaves the pill
         and its panel. The short delay lets the pointer cross the gap
         between the two. Below 1024 the bar is a sheet, so tap only. */
      var leaveTimer = null;
      drop.addEventListener('mouseenter', function () {
        if (!canHover()) return;
        window.clearTimeout(leaveTimer);
        closeAll(drop);
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
      });
      drop.addEventListener('mouseleave', function () {
        if (!canHover()) return;
        leaveTimer = window.setTimeout(function () {
          panel.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
        }, 180);
      });
    });

    /*
      An untouched price range would otherwise submit as
      `filter.v.price.gte=&filter.v.price.lte=`. Shopify ignores the empty
      pair, but it lands in the address bar and in anything that shares the
      URL, so the empty fields are disabled for the duration of the submit
      and restored on the next tick in case the navigation is cancelled.
    */
    function stripEmptyRanges() {
      var emptied = $$('input[type="number"]', form).filter(function (input) {
        return input.value === '';
      });
      emptied.forEach(function (input) {
        input.disabled = true;
      });
      window.setTimeout(function () {
        emptied.forEach(function (input) {
          input.disabled = false;
        });
      }, 0);
    }

    /* A real click on Apply; `form.submit()` below fires no submit event. */
    form.addEventListener('submit', stripEmptyRanges);

    /*
      Every choice applies immediately (2026-10-21, Design V6): a sort radio
      and a filter tick both submit on change, so neither panel carries an
      Apply button. Only the price range, which is typed, still waits for
      its own button.
    */
    $$('[data-mb-facet-input]', form).forEach(function (input) {
      if (input.type !== 'radio' && input.type !== 'checkbox') return;
      input.addEventListener('change', function () {
        if (input.type === 'radio' && !input.checked) return;
        stripEmptyRanges();
        form.submit();
      });
    });

    /* The applied-sort chip: back to the collection's default order. */
    $$('[data-mb-sort-clear]', form).forEach(function (chip) {
      chip.addEventListener('click', function () {
        var value = chip.getAttribute('data-mb-sort-clear');
        var radio = $$('input[name="sort_by"]', form).filter(function (r) { return r.value === value; })[0];
        if (radio) {
          radio.checked = true;
        } else {
          var hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'sort_by';
          hidden.value = value;
          form.appendChild(hidden);
        }
        stripEmptyRanges();
        form.submit();
      });
    });

    /* 2026-10-21: any click outside the open dropdown closes it, including
       the rest of the filter bar. Capture phase, so a card or an app that
       stops the click on its way up cannot keep the panel open. */
    document.addEventListener(
      'click',
      function (event) {
        var inside = drops.some(function (drop) {
          return drop.contains(event.target);
        });
        if (!inside) closeAll(null);
      },
      true
    );

    form.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      closeAll(null);
    });

    /*
      Below 1024 the frame collapses every group behind one "Filters"
      control. The groups are not duplicated for it â the same list becomes
      the sheet's contents â so a facet can never be submitted twice, and a
      phone without JavaScript keeps the plain list it was served.
    */
    var allTrigger = $('[data-mb-cgrid-all]', form);
    var groups = $('[data-mb-cgrid-filters]', form);

    if (allTrigger && groups) {
      var mq = window.matchMedia('(max-width: 1023px)');

      function syncMobile() {
        if (mq.matches) {
          allTrigger.hidden = false;
          groups.hidden = true;
          allTrigger.setAttribute('aria-expanded', 'false');
        } else {
          allTrigger.hidden = true;
          groups.hidden = false;
        }
        placeSort();
      }

      allTrigger.addEventListener('click', function () {
        var open = groups.hidden;
        groups.hidden = !open;
        allTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        section.classList.toggle('mb-cgrid--sheet-open', open);
        if (!open) closeAll(null);
      });

      /*
        2026-10-21: below 1024 the list is a bottom sheet (Figma "Mobile Â·
        Filter & Sort sheet"). Its close control and any tap outside it
        close it; every choice inside still applies on tap.
      */
      function closeSheet() {
        if (!mq.matches || groups.hidden) return;
        groups.hidden = true;
        allTrigger.setAttribute('aria-expanded', 'false');
        section.classList.remove('mb-cgrid--sheet-open');
      }
      $$('[data-mb-sheet-close]', form).forEach(function (btn) {
        btn.addEventListener('click', closeSheet);
      });
      document.addEventListener('click', function (event) {
        if (groups.contains(event.target) || allTrigger.contains(event.target)) return;
        closeSheet();
      });
      form.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeSheet();
      });

      /*
        The frame carrying the layout switch labels that one pill "Filters &
        Sort By" and has no separate sort control â the switch takes the room
        the second pill used. So below 1024 the sort dropdown moves INTO the
        drawer and comes back out above it. Moving the existing node rather
        than rendering a second copy is what keeps one `sort_by` input in the
        form: two would submit the facet twice.
      */
      var sortDrop = $('.mb-cdrop--sort', form);
      var sortHome = sortDrop ? sortDrop.parentNode : null;
      var sortNext = sortDrop ? sortDrop.nextSibling : null;

      function placeSort() {
        if (!sortDrop) return;
        if (mq.matches) {
          if (sortDrop.parentNode !== groups) groups.appendChild(sortDrop);
        } else if (sortDrop.parentNode !== sortHome) {
          sortHome.insertBefore(sortDrop, sortNext);
        }
      }

      syncMobile();
      if (mq.addEventListener) {
        mq.addEventListener('change', syncMobile);
      } else if (mq.addListener) {
        mq.addListener(syncMobile);
      }
    }
  }

  /**
   * The card's purchase control.
   *
   * A card with selling plans carries two radios and, when the plan group
   * offers more than one frequency, a select. The prices in the markup are
   * the variant's real `selling_plan_allocations`, so switching option is a
   * lookup rather than a calculation â the card can never quote a number
   * the cart will not honour.
   */
  function initCollectionCards(root) {
    $$('[data-mb-pcard]', root).forEach(function (card) {
      if (!once(card, 'pcard')) return;

      /*
        The hover image ships `loading="lazy"`, and a lazy image that is
        completely covered by another one is never fetched â all eight sat
        `complete: false` forever, so the swap revealed an empty box and read
        as "this card has no hover". Making it visible does not start the
        download either; only the loading attribute does.

        So it is promoted to eager once its own card is near the viewport:
        the first hover always has a picture to show, and the second image of
        a card nobody scrolls to is still never downloaded.
      */
      var hoverImg = $('.mb-pcard__img--hover', card);
      if (hoverImg && hoverImg.getAttribute('loading') === 'lazy') {
        var wake = function () {
          var box = card.getBoundingClientRect();
          if (box.bottom < -300 || box.top > window.innerHeight + 300) return;
          hoverImg.setAttribute('loading', 'eager');
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
          card.removeEventListener('pointerenter', wake);
        };
        var scrollTimer = null;
        var onScroll = function () {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(wake, 100);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        /* A pointer arriving first wins the race regardless of scrolling. */
        card.addEventListener('pointerenter', wake);
        wake();
      }

      /*
        Touch has no hover, so the purchase box needs a way in. On a coarse
        pointer the first tap on the add control OPENS the box instead of
        adding; once it is open the button behaves exactly as it always has,
        so the add path below is untouched and there is no second cart
        implementation.

        The listener is on the card in the CAPTURE phase, so it runs before
        the button's own click handler and can decide whether that handler
        should see the event at all. Only cards that actually have a purchase
        box take part â a plain card adds on the first tap, as before.
      */
      if (card.classList.contains('mb-pcard--plans')) {
        var coarse = function () {
          return window.matchMedia('(hover: none), (pointer: coarse)').matches;
        };
        card.addEventListener(
          'click',
          function (event) {
            var add = event.target.closest && event.target.closest('[data-mb-pcard-add]');
            if (!add || !card.contains(add)) return;
            if (!coarse()) return;
            if (card.classList.contains('mb-pcard--buyopen') && !pressOpened) return;
            pressOpened = false;
            card.classList.add('mb-pcard--buyopen');
            event.preventDefault();
            event.stopPropagation();
          },
          true
        );

        /*
          2026-10-21 (v2): the phone's stand-in for hover. The box opens as the
          finger lands on the add control, so pressing and holding shows it
          too, not only a quick tap (a held press never fires `click`). The
          click that ends that same press is swallowed above, so it never
          adds; the next tap adds. A press that turns into a scroll keeps it open; a tap elsewhere, another box opening or scrolling it out of view closes it.
        */
        var pressOpened = false;
        var pressTimer = null;
        card.addEventListener(
          'pointerdown',
          function (event) {
            if (event.pointerType === 'mouse' || !coarse()) return;
            var add = event.target.closest && event.target.closest('[data-mb-pcard-add]');
            if (!add || !card.contains(add)) return;
            if (card.classList.contains('mb-pcard--buyopen')) return;
            clearTimeout(pressTimer);
            pressOpened = true;
            card.classList.add('mb-pcard--buyopen');
          },
          true
        );
        card.addEventListener(
          'pointerup',
          function () {
            if (!pressOpened) return;
            clearTimeout(pressTimer);
            pressTimer = setTimeout(function () {
              pressOpened = false;
            }, 800);
          },
          true
        );
        card.addEventListener(
          'pointercancel',
          function () {
            /* v7: a light touch that turns into a scroll keeps the box open (Iram);
               only the click guard is reset, so the next tap adds. */
            pressOpened = false;
          },
          true
        );

        /*
          2026-10-21 (v3), mouse: a hover opens the box and it STAYS open, like
          the held press on a phone. It closes when the page scrolls, when the
          cursor moves clearly away (60px past the box and button), on a click
          elsewhere (the document listener above) or on Escape.
        */
        var buyArea = $('.mb-pcard__buy', card);
        var fine = function () {
          return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        };
        if (buyArea) {
          var hoverScrollY = 0;
          var stopHover = function () {
            document.removeEventListener('pointermove', onHoverMove);
            window.removeEventListener('scroll', onHoverScroll);
            document.removeEventListener('keydown', onHoverKey);
          };
          var closeHover = function () {
            card.classList.remove('mb-pcard--buyopen');
            stopHover();
          };
          var onHoverMove = function (event) {
            if (event.pointerType === 'touch') return;
            var area = buyArea.getBoundingClientRect();
            var box = $('.mb-pcard__plans', card);
            var top = box ? Math.min(area.top, box.getBoundingClientRect().top) : area.top;
            var slack = 60;
            if (
              event.clientX < area.left - slack ||
              event.clientX > area.right + slack ||
              event.clientY < top - slack ||
              event.clientY > area.bottom + slack
            ) {
              closeHover();
            }
          };
          var onHoverScroll = function () {
            if (Math.abs(window.scrollY - hoverScrollY) > 24) closeHover();
          };
          var onHoverKey = function (event) {
            if (event.key === 'Escape') closeHover();
          };
          buyArea.addEventListener('pointerenter', function (event) {
            if (event.pointerType === 'touch') return;
            if (card.classList.contains('mb-pcard--buyopen')) return;
            card.classList.add('mb-pcard--buyopen');
            hoverScrollY = window.scrollY;
            document.addEventListener('pointermove', onHoverMove);
            window.addEventListener('scroll', onHoverScroll, { passive: true });
            document.addEventListener('keydown', onHoverKey);
          });
        }
        /*
          2026-10-21 (v8): one box open at a time (opening another card's box
          closes this one), and a box closes once its button has scrolled out
          of view; a small scroll with the button still on screen leaves it open.
        */
        new MutationObserver(function () {
          if (card.classList.contains('mb-pcard--buyopen')) {
            document.dispatchEvent(new CustomEvent('mb:pcard-open', { detail: card }));
          }
        }).observe(card, { attributes: true, attributeFilter: ['class'] });
        document.addEventListener('mb:pcard-open', function (event) {
          if (event.detail !== card) card.classList.remove('mb-pcard--buyopen');
        });
        if (buyArea && 'IntersectionObserver' in window) {
          new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) card.classList.remove('mb-pcard--buyopen');
            });
          }).observe(buyArea);
        }

        /* A tap anywhere else puts the card back to its resting state. */
        document.addEventListener('click', function (event) {
          if (card.contains(event.target)) return;
          card.classList.remove('mb-pcard--buyopen');
        });
      }

      var button = $('[data-mb-pcard-add]', card);
      if (!button) return;

      var label = $('[data-mb-pcard-add-price]', card);
      var planInput = $('[data-mb-pcard-plan]', card);
      var planPrice = $('[data-mb-pcard-plan-price]', card);
      var planSave = $('[data-mb-pcard-save]', card);
      var planCompare = $('[data-mb-pcard-plan-compare]', card);
      var variantId = Number(card.getAttribute('data-mb-pcard-variant'));
      var onetime = $('[data-mb-pcard-mode="onetime"]', card);

      function currentPlan() {
        if (!planInput) return null;
        if (onetime && onetime.checked) return null;
        /* A hidden input carrying `value` + `data-price`. The custom
           frequency listbox below keeps both in step, so everything
           downstream reads one shape. */
        return planInput;
      }

      function currentPrice() {
        var option = currentPlan();
        if (option) return Number(option.getAttribute('data-price'));
        /* One-time, or a card with no plans at all: the variant's own price
           is already in the button, so nothing has to be recomputed. */
        return null;
      }

      function repaint() {
        var option = currentPlan();
        var price = currentPrice();

        if (planPrice && option) {
          planPrice.textContent = formatCardMoney(Number(option.getAttribute('data-price')));
        }

        /* The pill and the struck price belong to the plan on show (the
           subscribe row keeps them when one-time is chosen), so they follow
           the hidden input: its price against its own compare-at. */
        if (planInput) {
          var planCents = Number(planInput.getAttribute('data-price'));
          var compareCents = Number(planInput.getAttribute('data-compare'));
          var saved = compareCents - planCents;
          var pct = compareCents > 0 && saved > 0 ? Math.round((saved * 100) / compareCents) : 0;
          if (planSave) {
            planSave.hidden = pct <= 0;
            planSave.textContent = (planSave.getAttribute('data-label') || '') + ' ' + pct + '%';
          }
          if (planCompare) {
            planCompare.hidden = saved <= 0;
            if (saved > 0) planCompare.textContent = formatCardMoney(compareCents);
          }
        }

        if (!label) return;
        if (price === null) {
          /* One-time: reuse the price already rendered beside that radio,
             so the button never disagrees with the row above it. */
          var oneTimeRow = onetime ? onetime.closest('.mb-pcard__plan') : null;
          var oneTimeValue = oneTimeRow ? $('.mb-pcard__now', oneTimeRow) : null;
          if (oneTimeValue) label.textContent = oneTimeValue.textContent.trim();
        } else {
          label.textContent = formatCardMoney(price);
        }
      }

      $$('[data-mb-pcard-mode]', card).forEach(function (input) {
        input.addEventListener('change', repaint);
      });

      /*
        The frequency control. The frame draws a 54 x 16 box with its own
        chevron, which a native `<select>` cannot be sized to and whose
        option list the OS draws â so it is a listbox, and this is what
        makes it behave like one: click or Enter/Space to open, arrows and
        Home/End to move, Enter to take, Escape to close, click-away to
        dismiss. Choosing an option writes the plan id and price back to
        the hidden input and repaints, so the purchase logic never learns
        the control changed shape.
      */
      var freq = $('[data-mb-freq]', card);
      if (freq) {
        var freqTrigger = $('[data-mb-freq-trigger]', freq);
        var freqPanel = $('[data-mb-freq-panel]', freq);
        var freqLabel = $('[data-mb-freq-label]', freq);
        var freqOptions = $$('[data-mb-freq-option]', freq);

        var openFreq = function (open) {
          if (!freqPanel || !freqTrigger) return;
          freqPanel.hidden = !open;
          freqTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (open) {
            var current = freqOptions.filter(function (o) {
              return o.getAttribute('aria-selected') === 'true';
            })[0];
            (current || freqOptions[0]) && (current || freqOptions[0]).focus();
          }
        };

        var chooseFreq = function (option) {
          if (!option) return;
          freqOptions.forEach(function (o) {
            o.setAttribute('aria-selected', o === option ? 'true' : 'false');
          });
          planInput.value = option.getAttribute('data-value');
          planInput.setAttribute('data-price', option.getAttribute('data-price'));
          planInput.setAttribute('data-compare', option.getAttribute('data-compare'));
          if (freqLabel) freqLabel.textContent = option.textContent.trim();
          repaint();
          openFreq(false);
          if (freqTrigger) freqTrigger.focus();
        };

        var moveFreq = function (from, step) {
          var i = freqOptions.indexOf(from);
          var next = freqOptions[Math.max(0, Math.min(freqOptions.length - 1, i + step))];
          if (next) next.focus();
        };

        if (freqTrigger) {
          freqTrigger.addEventListener('click', function () {
            openFreq(freqPanel.hidden);
          });
          freqTrigger.addEventListener('keydown', function (event) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              openFreq(true);
            }
          });
        }

        freqOptions.forEach(function (option) {
          option.addEventListener('click', function () {
            chooseFreq(option);
          });
          option.addEventListener('keydown', function (event) {
            switch (event.key) {
              case 'Enter':
              case ' ':
                event.preventDefault();
                chooseFreq(option);
                break;
              case 'ArrowDown':
                event.preventDefault();
                moveFreq(option, 1);
                break;
              case 'ArrowUp':
                event.preventDefault();
                moveFreq(option, -1);
                break;
              case 'Home':
                event.preventDefault();
                freqOptions[0].focus();
                break;
              case 'End':
                event.preventDefault();
                freqOptions[freqOptions.length - 1].focus();
                break;
              case 'Escape':
                event.preventDefault();
                openFreq(false);
                if (freqTrigger) freqTrigger.focus();
                break;
            }
          });
        });

        document.addEventListener('click', function (event) {
          if (!freq.contains(event.target)) openFreq(false);
        });
      }

      button.addEventListener('click', function () {
        if (button.disabled || !variantId) return;
        var option = currentPlan();
        var payload = { id: variantId, quantity: 1 };
        if (option) {
          var planId = Number(option.value);
          if (planId) payload.selling_plan = planId;
        }

        button.classList.add('is-loading');
        addToCart({ items: [payload] })
          .then(announceCartAdd)
          .catch(function () {})
          .then(function () {
            button.classList.remove('is-loading');
          });
      });
    });
  }

  /**
   * "Load More".
   *
   * The button is a real link to the next paginated page, so it works
   * without JavaScript. With it, the next page is fetched through the
   * Section Rendering API and its cards are appended in place â the grid
   * keeps its scroll position and the URL stays on page one.
   */
  function initCollectionMore(section) {
    var button = $('[data-mb-cgrid-more]', section);
    var grid = $('[data-mb-cgrid-items]', section);
    if (!button || !grid) return;

    button.addEventListener('click', function (event) {
      event.preventDefault();
      if (button.classList.contains('is-loading')) return;

      var url = button.getAttribute('href');
      if (!url) return;

      var sectionId = button.getAttribute('data-mb-cgrid-section');
      var joiner = url.indexOf('?') === -1 ? '?' : '&';
      button.classList.add('is-loading');

      fetch(url + joiner + 'section_id=' + encodeURIComponent(sectionId))
        .then(function (response) {
          return response.ok ? response.text() : null;
        })
        .then(function (html) {
          if (!html) return;
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var nextGrid = parsed.querySelector('[data-mb-cgrid-items]');
          var nextButton = parsed.querySelector('[data-mb-cgrid-more]');

          if (nextGrid) {
            while (nextGrid.firstElementChild) {
              grid.appendChild(nextGrid.firstElementChild);
            }
            initCollectionCards(grid);
          }

          if (nextButton) {
            button.setAttribute('href', nextButton.getAttribute('href'));
          } else {
            var wrap = button.closest('.mb-cgrid__more');
            if (wrap) wrap.remove();
          }
        })
        .catch(function () {})
        .then(function () {
          button.classList.remove('is-loading');
        });
    });
  }

  /**
   * The mobile grid/list switch.
   *
   * Only the column count and the card's own scale differ between the two
   * states â same markup, same products, same handlers â so switching is a
   * class on the section, not a re-render. Nothing is fetched, nothing is
   * replaced, and every card keeps the listeners and the purchase state it
   * already had.
   *
   * The choice is remembered per browser. `localStorage` can throw outright
   * (private windows, blocked site data), so every access is guarded and the
   * switch simply stops persisting rather than failing.
   */
  /* v2 (2026-10-21): a fresh key, so a list choice saved before the
     two-up grid became the default no longer overrides it. */
  var VIEW_KEY = 'mb:collection-view-v2';

  function initCollectionView(section) {
    var control = $('[data-mb-cview]', section);
    if (!control) return;

    var buttons = $$('[data-mb-cview-btn]', control);
    if (!buttons.length) return;

    var mq = window.matchMedia('(max-width: 1023px)');

    function stored() {
      try {
        return window.localStorage.getItem(VIEW_KEY);
      } catch (e) {
        return null;
      }
    }

    function remember(value) {
      try {
        window.localStorage.setItem(VIEW_KEY, value);
      } catch (e) {
        /* Nothing to do: the switch still works for this page view. */
      }
    }

    function apply(value) {
      /* List (one 343 card a row) is the default: the two-up grid card drops
         its type to 8-12px. The markup ships with the list class. */
      section.classList.toggle('mb-cgrid--view-list', value === 'list');
      buttons.forEach(function (button) {
        var mine = button.getAttribute('data-mb-cview-btn');
        button.setAttribute('aria-pressed', mine === value ? 'true' : 'false');
      });
    }

    function syncVisible() {
      control.hidden = !mq.matches;
    }

    buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        var value = button.getAttribute('data-mb-cview-btn');
        apply(value);
        remember(value);
      });
    });

    /* 2026-10-21: the two-up grid is the default; list only once chosen. */
    apply(stored() === 'list' ? 'list' : 'grid');
    syncVisible();

    /*
      Driven off `resize` rather than a `matchMedia` change listener:
      emulators flip `matches` without ever dispatching `change`.
    */
    var timer = null;
    window.addEventListener('resize', function () {
      clearTimeout(timer);
      timer = setTimeout(syncVisible, 150);
    });
  }

  function initCollection(root) {
    $$('[data-mb-cgrid]', root).forEach(function (section) {
      if (!once(section, 'cgrid')) return;
      initCollectionFacets(section);
      initCollectionMore(section);
      initCollectionView(section);
    });
    initCollectionCards(root);
  }

  /**
   * "Scroll to top" links (the CTA's `scroll_top` action). A real
   * `#MainContent` link underneath, so it still lands without script; with
   * it the page glides back up instead of jumping, and the URL keeps no
   * stray hash. Reduced-motion users get the jump.
   */
  function initScrollTop(root) {
    $$('[data-mb-scroll-top]', root).forEach(function (link) {
      if (!once(link, 'scrolltop')) return;
      link.addEventListener('click', function (event) {
        event.preventDefault();
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
      });
    });

    /* In-page anchors (the CTA's "this page's products"): a smooth scroll that
       lands under the sticky header via the target's `scroll-margin-top`.
       Pinned sections passed on the way (the product page's ingredient hold)
       change the page's height mid-scroll, so once the scroll settles it
       re-aims at the target's real position, a few times at most. */
    $$('[data-mb-scroll-to]', root).forEach(function (link) {
      if (!once(link, 'scrollto')) return;
      link.addEventListener('click', function (event) {
        var target = document.querySelector(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        var behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        var tries = 0;

        function miss() {
          var margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
          return target.getBoundingClientRect().top - margin;
        }

        function aim() {
          window.scrollTo({ top: window.scrollY + miss(), behavior: behavior });
          settle(window.scrollY);
        }

        function settle(last) {
          window.setTimeout(function () {
            if (Math.abs(window.scrollY - last) > 1) return settle(window.scrollY);
            if (Math.abs(miss()) > 2 && tries++ < 3) aim();
          }, 150);
        }

        aim();
      });
    });
  }

  /* ------------------------------------------------------------ timeline */

  /* The rail's gold. Desktop (days in a row): it fills Day 7 to Day 90 with
     the page's scroll. Mobile (days in a swipeable strip): it follows the
     strip itself - Figma's resting fill (to 47% of the screen) at the first
     day, 100% at the last - and eases in from empty the first time the rail
     comes into view, then tracks every swipe and drag. Set only here, so
     without script (or with reduced motion) the rail reads full. */
  function initTimeline(root) {
    $$('[data-mb-tl-progress]', root).forEach(function (track) {
      if (!once(track, 'tlprogress')) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      var rail = $('.mb-tl__dot', track) || track;
      var days = $$('.mb-tl__day', track);
      var count = days.length || 1;
      var seen = !('IntersectionObserver' in window);
      var queued = false;

      function stripProgress() {
        var max = track.scrollWidth - track.clientWidth;
        var step = count > 1 ? days[1].offsetLeft - days[0].offsetLeft : track.clientWidth;
        var start = Math.min(count, (track.clientWidth * 0.47) / step);
        var ratio = max > 0 ? track.scrollLeft / max : 1;
        return (start + (count - start) * ratio) / count;
      }

      /* Empty while the rail sits at 90% of the viewport height, full by 65% -
         a short scroll, so the whole rail is gold while it is still in view. */
      function pageProgress() {
        var vh = window.innerHeight;
        return (vh * 0.9 - rail.getBoundingClientRect().top) / (vh * 0.25);
      }

      function update() {
        queued = false;
        var strip = track.scrollWidth > track.clientWidth + 1;
        var p = strip ? (seen ? stripProgress() : 0) : pageProgress();
        track.style.setProperty('--mb-tl-p', Math.min(1, Math.max(0, p)).toFixed(4));
      }

      function queue() {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(update);
      }

      if (!seen) {
        var watcher = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              seen = true;
              watcher.disconnect();
              if (track.scrollWidth <= track.clientWidth + 1) return;
              track.classList.add('is-easing');
              update();
              window.setTimeout(function () {
                track.classList.remove('is-easing');
              }, 900);
            });
          },
          { threshold: 1, rootMargin: '0px 0px -15% 0px' }
        );
        watcher.observe(rail);
      }

      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue);
      track.addEventListener('scroll', queue, { passive: true });
      update();
    });
  }

  /* ------------------------------------------------------ page end flag */

  /* While the footer is on screen `mb-at-footer` goes on <html>, so the
     Klaviyo corner teaser turns into a side tab clear of the footer wordmark. */
  var atEndBound = false;
  function initPageEnd() {
    if (atEndBound || !('IntersectionObserver' in window)) return;
    var footer = document.querySelector('[data-mb-footer]');
    if (!footer) return;
    atEndBound = true;
    new IntersectionObserver(function (entries) {
      document.documentElement.classList.toggle('mb-at-footer', entries[entries.length - 1].isIntersecting);
    }).observe(footer);
  }

  function initAll(root) {
    var scope = root || document;
    initHeader(scope);
    initGallery(scope);
    initWishlist(scope);
    initAccordions(scope);
    initProduct(scope);
    initUpsell(scope);
    initFaq(scope);
    initFaqMore(scope);
    initCompare(scope);
    initIngredients(scope);
    initCarousels(scope);
    initRoutine(scope);
    initCollection(scope);
    initMarquee(scope);
    initSpotlightCrop(scope);
    initRatings(scope);
    initSteps(scope);
    initUgc(scope);
    initVideoWall(scope);
    initTestimonials(scope);
    initTimeline(scope);
    initFooter(scope);
    initScrollTop(scope);
    initPageEnd();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* Theme Editor: sections can be re-rendered at any time. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  /* A section about to be replaced drops its scroll triggers first, so the
     re-rendered copy starts clean. */
  document.addEventListener('shopify:section:unload', function (event) {
    $$('[data-mb-ingredients]', event.target).forEach(function (section) {
      if (section.__mbIngMM) {
        section.__mbIngMM.revert();
        section.__mbIngMM = null;
      }
    });
  });
  document.addEventListener('shopify:section:select', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    var trigger = event.target.querySelector('[data-mb-accordion-trigger]');
    var panel = event.target.querySelector('[data-mb-accordion-panel]');
    if (trigger && panel) {
      trigger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('data-open', 'true');
    }
  });
})();
